import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { TypingDots as SharedTypingDots } from '@/components/chat/TypingDots';
import TaskList from '@/components/Tasks/TaskList';
import TaskDetailPanel, { type TaskDetailTab } from '@/components/Tasks/TaskDetailPanel';
import { NoteDetailContent } from '@/components/notes/NoteDetailContent';
import { useDeleteNoteMutation } from '@/redux/internal-services/notesApi';
import WorkspaceMeetingList from '@/components/Meetings/WorkspaceMeetingList';
import WorkspacePropertyList from '@/components/Properties/WorkspacePropertyList';
import MeetingDetailPanel from '@/components/Meetings/MeetingDetailPanel';
import WorkflowCard from '@/components/Workflow/WorkflowCard';
import {
  useCreateEngagementMutation,
  useListAvailableTransitionsQuery,
  useGetWorkflowCustomizationQuery,
  useGetEngagementWorkflowStateQuery,
  useGetEngagementActivityQuery,
  useGetEngagementByIdQuery,
  type EngagementActivity,
} from '@/redux/internal-services/workflowEngineApi';
import { useListCatalogueEntriesQuery } from '@/redux/internal-services/platformAdminApi';
import { workspaceApi, refreshWorkspaceModule } from '@/redux/internal-services/workspaceApi';
import { useDispatch } from 'react-redux';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { WORKSPACE_QUERY_OPTIONS } from '@/components/chat/workspaceQueryOptions';
import {
  useGetTaskByIdQuery, useGetMeetingByIdQuery, useGetPropertyDetailQuery,
  useGetPropertyMediaQuery,
  useGetPropertyMeasurementsQuery, useGetPropertyNotesQuery, useGetPropertyContactsQuery,
  useUploadMediaFileMutation, useAttachPropertyMediaMutation,
  useCreatePropertyMeasurementMutation,
  useCreatePropertyNoteMutation,
  useGetTasksListQuery,
  useClaimTaskMutation, type TaskFull, type MeetingFull, type PropertyContact,
} from '@/redux/internal-services/workspaceApiExtensions';
import TaskCard, { ContextTypeIcon } from '@/components/Tasks/TaskCard';
import WorkspaceMeetingCard from '@/components/Meetings/WorkspaceMeetingCard';
import { Spinner } from '@/components/UI/Spinner';
import { CardSection } from '@/components/UI/CardSection';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { SamFacet, SamModule } from '@/components/SAM/samActionTypes';
import { humanizeLabel, sv, cardId, cardInfo, fmtDate, STATUS_OPTIONS, HIDDEN, DATE_FIELDS, type Rec } from '@/components/chat/ChatPills';
import { CPill } from '@/components/chat/ChatPills';
import { viewFallbackLabel, type RecentEntry } from '@/components/chat/recentViews';
import CalendarPage from '@/pages/calendar/CalendarPage';
import { NotificationItem } from '@/components/Notifications';
import { useGetInboxQuery, useGetInboxCountsQuery, type InAppNotificationOut } from '@/redux/internal-services/notificationsApi';
import ApprovalDetailPanel from '@/components/Workflow/ApprovalDetailPanel';
import { useGetApprovalByIdQuery } from '@/redux/internal-services/workflowEngineApi';

// ─── types ────────────────────────────────────────────────────────────────────

export interface CardsMessage {
  kind: 'cards';
  id: string;
  module: SamModule;
  records: Record<string, unknown>[];
  filter?: Record<string, unknown>;
  facets?: SamFacet[];
  /** LLM-authored heading shown in the workspace header (falls back to the module name). */
  title?: string;
  /** LLM-authored one-line summary, revealed via the workspace "⋯" menu. */
  caption?: string;
  /** Set on a reload-restored placeholder whose rows still need to be fetched. */
  hydrate?: boolean;
  /** Turn id that produced this view — used to show the matching assistant reply above the cards. */
  messageId?: string;
}

export type OnSave = (module: SamModule, id: string, draft: Rec) => void;
export type OnOpenChange = (id: string | null) => void;
export type OnFacet = (module: SamModule, facet: SamFacet) => void;

// The "⋯" menu's entries are `RecentEntry` (see `@/components/chat/recentViews`) — one row per
// DISTINCT view, backed by the `workspace_recent_views` collection. What used to be flag fields
// on the old local-only `ViewHistoryEntry` (communicationId / engagementId / workflowSettings /
// workflowList) is now one `contextRef: {type, id}`, so `replayView` switches on `contextRef.type`.
export type { RecentEntry } from '@/components/chat/recentViews';

/** A workspace view the user opened with no agent turn behind it — reported to `useRecentViews`
 *  so it joins the same "⋯" menu as the agent's. */
export interface UserViewReport {
  module: SamModule;
  params?: Record<string, unknown>;
  title?: string;
  subtitle?: string;
  /** The record the view is bound to (a property/engagement/…), when it isn't a plain list. */
  recordId?: string | null;
}

/** Carries `onUserView` to deeply-nested cards (the engagement `+`, a detail panel) without
 *  threading the prop through every intermediate component. */
const UserViewContext = React.createContext<((view: UserViewReport) => void) | undefined>(undefined);

// ─── Reply strip (SAM's latest message above the workspace card on mobile) ───────

/** Three-dot "SAM is replying" indicator, sized for the compact reply-strip contexts here —
 *  same shared component the chat thread's TypingIndicator uses (ChatThread.tsx). */
const TypingDots = () => <SharedTypingDots dotClassName="w-1.5 h-1.5 bg-brand-dark" />;

export function ReplyStrip({ text, onExpand, isWaiting }: { text: string; onExpand?: () => void; isWaiting?: boolean }) {
  if (!text && !isWaiting) return null;
  return (
    <div
      className={`flex items-start gap-2 px-4 py-2 border-b border-ui-border bg-ui-subtle text-ui-text2 text-xs shrink-0 ${isWaiting ? '' : 'cursor-pointer'}`}
      onClick={isWaiting ? undefined : onExpand}
    >
      {isWaiting ? <TypingDots /> : <span className="flex-1 line-clamp-2">{text}</span>}
      {onExpand && !isWaiting && <span className="shrink-0 underline">View</span>}
    </div>
  );
}

// ─── History menu (the "⋯" in the workspace header) ─────────────────────────────

export function HistoryMenu({ history, caption, onReplay, hasMore, onLoadMore }: {
  history: RecentEntry[];
  caption?: string;
  onReplay?: (e: RecentEntry) => void;
  hasMore?: boolean;
  onLoadMore?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Watch sentinel div with IntersectionObserver — triggers onLoadMore when scrolled into view.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !onLoadMore || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) onLoadMore(); },
      { threshold: 0.1 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [open, hasMore, onLoadMore]);

  // The "⋯" lists every distinct view this session — title + caption — clickable to re-open.
  if (!history.length && !caption) return null;
  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title={caption || 'Session actions'}
        aria-label="Session actions"
        className="rounded-lg px-2 py-1 text-ui-text2 hover:bg-ui-hover hover:text-ui-text transition"
        style={{ fontSize: 18, lineHeight: 1 }}
      >
        &#8943;
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 20 }} onClick={() => setOpen(false)} aria-hidden />
          <div
            style={{ position: 'absolute', right: 0, top: '110%', zIndex: 30, width: 320, maxHeight: 380, overflowY: 'auto' }}
            className="no-scrollbar rounded-xl border border-ui-border bg-ui-card py-1 shadow-xl"
          >
            <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-ui-text3">This session</p>
            {history.length > 0 ? (
              <>
                {/* No client-side dedupe pass: entries arrive already deduped by `recentViewKey`
                    (module + kind + bound record + filter). The old filter here compared TITLES,
                    which both merged genuinely different views that happened to share a heading
                    and failed to merge identical views the agent captioned differently. */}
                {history.map((e) => (
                  <button
                    key={e.key}
                    onClick={() => { onReplay?.(e); setOpen(false); }}
                    // The row shows a compact relative age; the exact time is on hover, since
                    // "now"/"2m" collapses everything recent into the same label.
                    title={absTime(e.lastAccessedAt)}
                    className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-ui-hover transition"
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium text-ui-text">{e.title || viewFallbackLabel(e)}</span>
                      <span className="flex-shrink-0 text-[10px] text-ui-text3">{relTime(e.lastAccessedAt)}</span>
                    </div>
                    {e.subtitle && <span className="text-xs leading-snug text-ui-text2">{e.subtitle}</span>}
                  </button>
                ))}
                {hasMore && (
                  <div ref={sentinelRef} className="flex justify-center py-2">
                    <span className="text-[10px] text-ui-text3">Loading more…</span>
                  </div>
                )}
              </>
            ) : (
              caption && <p className="px-3 py-2 text-xs leading-relaxed text-ui-text2">{caption}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** Exact local date + time an entry was last opened — the "⋯" row's hover tooltip. */
function absTime(ts: number): string {
  if (!ts) return '';
  return new Date(ts).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function relTime(ts: number): string {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'now';
  const m = Math.floor(s / 60); if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

// ─── WorkspaceRefreshButton ───────────────────────────────────────────────────
// Sits immediately before the HistoryMenu (⋯) in the workspace header.
// Dispatches refreshWorkspaceModule which invalidates the right RTK Query tags
// for the current module + open detail id, without touching tag descriptors in UI code.

function WorkspaceRefreshButton({ module, openId, onRefreshChange }: {
  module: SamModule;
  openId?: string | null;
  onRefreshChange?: (refreshing: boolean) => void;
}) {
  const dispatch = useAppDispatch();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    onRefreshChange?.(true);
    dispatch(refreshWorkspaceModule(module, openId));
    setTimeout(() => {
      setIsRefreshing(false);
      onRefreshChange?.(false);
    }, 1500);
  };

  return (
    <button
      type="button"
      onClick={handleRefresh}
      aria-label="Refresh"
      title={isRefreshing ? 'Refreshing…' : 'Refresh'}
      className={`rounded-lg px-1.5 py-1 transition-all ${
        isRefreshing
          ? 'text-ui-brand bg-brand-dark/10 scale-110'
          : 'text-ui-text3 hover:bg-ui-hover hover:text-ui-text'
      }`}
    >
      <svg
        width="14" height="14" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round"
        aria-hidden
        className={isRefreshing ? 'animate-spin' : ''}
      >
        <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
        <path d="M3 3v5h5"/>
        <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/>
        <path d="M16 16h5v5"/>
      </svg>
    </button>
  );
}

// ─── InlineDetail ─────────────────────────────────────────────────────────────

const INPUT_STYLE: React.CSSProperties = {
  background: 'var(--chat-overlay)',
  border: '0.5px solid var(--chat-border)',
  borderRadius: 6,
  color: 'var(--chat-text)',
  fontSize: 12,
  padding: '5px 8px',
  width: '100%',
  outline: 'none',
};

function InlineDetail({ record, module, onBack, onSave }: {
  record: Rec;
  module: SamModule;
  onBack: () => void;
  onSave?: OnSave;
}) {
  const [draft, setDraft] = useState<Rec>(() => ({ ...record }));
  const entries = Object.entries(record).filter(([k, v]) => !HIDDEN.has(k) && v !== null && v !== '' && typeof v !== 'object');
  const addr = (record.address as Rec) || null;
  const set = (k: string, v: string) => setDraft(d => ({ ...d, [k]: v }));
  // The UX panel is READ-ONLY for every module — all record changes go through SAM (e.g. rename
  // via update_user), never direct inline edits. So no module renders the edit form / Save button.
  const allowEdit = false;

  const renderField = (k: string, v: unknown) => {
    const val = String(draft[k] ?? v ?? '');
    if (k === 'status') {
      const opts = STATUS_OPTIONS[module] || ['active', 'inactive'];
      return (
        <select value={val} onChange={e => set(k, e.target.value)} style={{ ...INPUT_STYLE, cursor: 'pointer' }}>
          {opts.map(o => <option key={o} value={o} style={{ background: 'rgb(var(--color-chat-raised))' }}>{humanizeLabel(o)}</option>)}
        </select>
      );
    }
    if (k === 'priority') {
      return (
        <select value={val} onChange={e => set(k, e.target.value)} style={{ ...INPUT_STYLE, cursor: 'pointer' }}>
          {['high', 'medium', 'low'].map(o => <option key={o} value={o} style={{ background: 'rgb(var(--color-chat-raised))' }}>{humanizeLabel(o)}</option>)}
        </select>
      );
    }
    if (k === 'description' || k === 'notes') {
      return <textarea value={val} onChange={e => set(k, e.target.value)} rows={3} style={{ ...INPUT_STYLE, resize: 'vertical' as const }} />;
    }
    if (DATE_FIELDS.has(k)) {
      return <input type="date" value={val.slice(0, 10)} onChange={e => set(k, e.target.value)} style={INPUT_STYLE} />;
    }
    return <input type="text" value={val} onChange={e => set(k, e.target.value)} style={INPUT_STYLE} />;
  };

  return (
    <div style={{ background: 'var(--chat-overlay)', borderRadius: 12, border: '0.5px solid var(--chat-border)', padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'rgb(var(--color-brand-bright))', fontSize: 13, padding: 0, cursor: 'pointer' }}>
          ← Back
        </button>
        {allowEdit && (
          <button
            onClick={() => { onSave!(module, cardId(module, record), draft); onBack(); }}
            style={{ background: 'rgb(var(--color-brand-dark))', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 600, padding: '5px 16px', cursor: 'pointer' }}
          >
            Save
          </button>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {entries.map(([k, v]) => (
          <div key={k}>
            <p style={{ color: 'var(--chat-text-muted)', fontSize: 11, marginBottom: 4 }}>{humanizeLabel(k)}</p>
            {allowEdit ? renderField(k, v) : <p style={{ color: 'var(--chat-text)', fontSize: 13 }}>{sv(v)}</p>}
          </div>
        ))}
        {addr && (
          <div>
            <p style={{ color: 'var(--chat-text-muted)', fontSize: 11, marginBottom: 4 }}>Address</p>
            <p style={{ color: 'var(--chat-text)', fontSize: 13 }}>
              {[sv(addr.street), sv(addr.city), sv(addr.state), sv(addr.postal_code)].filter(Boolean).join(', ') || '—'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── FacetBar ─────────────────────────────────────────────────────────────────

function FacetBar({ facets, activeKey, onPick }: { facets: SamFacet[]; activeKey: string; onPick: (f: SamFacet) => void }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const, marginBottom: 10 }}>
      {facets.map(f => {
        const active = f.key === activeKey;
        return (
          <button
            key={f.key}
            onClick={() => onPick(f)}
            style={{
              background: active ? 'rgb(var(--color-brand-bright) / 0.22)' : 'var(--chat-overlay)',
              border: `0.5px solid ${active ? 'rgb(var(--color-brand-bright) / 0.6)' : 'var(--chat-border)'}`,
              color: active ? 'rgb(var(--color-brand-light))' : 'var(--chat-text-dim)',
              borderRadius: 999, fontSize: 12, fontWeight: 500, padding: '4px 12px', cursor: 'pointer',
              whiteSpace: 'nowrap' as const, transition: 'all 0.15s',
            }}
          >
            {f.label} ({f.count})
          </button>
        );
      })}
    </div>
  );
}

// ─── InlineCards ─────────────────────────────────────────────────────────────

export function InlineCards({ module, records, facets, onSave, openedId, onOpenChange, onFacet }: {
  module: SamModule; records: Rec[]; facets?: SamFacet[]; onSave?: OnSave;
  openedId?: string | null; onOpenChange?: OnOpenChange; onFacet?: OnFacet;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeFacet, setActiveFacet] = useState<string>('all');

  useEffect(() => {
    if (openedId !== undefined) setSelectedId(openedId);
  }, [openedId]);

  const select = (id: string | null) => { setSelectedId(id); onOpenChange?.(id); };
  const selected = selectedId ? records.find(r => cardId(module, r) === selectedId) ?? null : null;

  const pickFacet = (f: SamFacet) => { setActiveFacet(f.key); onFacet?.(module, f); };

  return (
    <div style={{ borderRadius: 16, padding: 12, marginTop: 4 }}>
      {selected ? (
        <InlineDetail record={selected} module={module} onBack={() => select(null)} onSave={onSave} />
      ) : (
        <>
          <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--chat-text-muted)', textTransform: 'uppercase' as const, letterSpacing: 1, marginBottom: 8, paddingLeft: 2 }}>
            {module} &middot; {records.length}
          </p>
          {facets && facets.length > 1 && (
            <FacetBar facets={facets} activeKey={activeFacet} onPick={pickFacet} />
          )}
          {records.map((r, idx) => {
            const { title, sub, priority, status, location } = cardInfo(module, r) as ReturnType<typeof cardInfo> & { location?: string };
            return (
              <div
                key={cardId(module, r)}
                onClick={() => select(cardId(module, r))}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(cardId(module, r)); } }}
                className="transition hover:brightness-95"
                style={{ background: 'var(--chat-overlay)', border: '0.5px solid var(--chat-border)', borderRadius: 12, padding: '12px 14px', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, width: '100%', textAlign: 'left' as const, cursor: 'pointer' }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontSize: 11, color: 'var(--chat-text-muted)', flexShrink: 0 }}>{idx + 1}</span>
                    <p style={{ fontWeight: 500, fontSize: 14, margin: '0 0 3px', color: 'var(--chat-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{title}</p>
                  </div>
                  {location && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, paddingLeft: 18, marginBottom: sub ? 2 : 0 }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary, #7c3aed)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                      </svg>
                      <span style={{ fontSize: 11, color: 'var(--color-primary, #7c3aed)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{location}</span>
                    </div>
                  )}
                  {sub && <p style={{ fontSize: 12, color: 'var(--chat-text-dim)', margin: 0, paddingLeft: 18 }}>{sub}</p>}
                </div>
                <div style={{ display: 'flex', gap: 5, flexShrink: 0, flexWrap: 'wrap' as const, justifyContent: 'flex-end' }}>
                  {priority && <CPill label={humanizeLabel(priority)} />}
                  {status && <CPill label={humanizeLabel(status)} />}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

// ─── MyTodosList ─────────────────────────────────────────────────────────────

const PRIORITY_DOT: Record<string, { dot: string; text: string; label: string }> = {
  low:    { dot: '#9ca3af', text: '#6b7280',  label: 'Low' },
  normal: { dot: '#60a5fa', text: '#2563eb',  label: 'Normal' },
  high:   { dot: '#f59e0b', text: '#d97706',  label: 'High' },
  urgent: { dot: '#ef4444', text: '#dc2626',  label: 'Urgent' },
};

// ─── QueueRow — compact card for role-queue tasks (still has the Claim button) ─

function QueueRow({ r, onNavigate, onClaim, claiming, claimError }: {
  r: Rec; onNavigate: () => void; onClaim: () => void; claiming: boolean; claimError: boolean;
}) {
  const title = sv(r['title']) || 'Task';
  const priority = sv(r['priority']);
  const dueOn = sv(r['due_on']);
  const contextLabel = sv(r['context_label']);
  const contextType = sv(r['context_type']);
  const pc = PRIORITY_DOT[priority] ?? PRIORITY_DOT.normal;
  const startStr = dueOn
    ? new Date(dueOn).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : null;

  return (
    <>
      <div
        onClick={onNavigate}
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer',
          background: 'var(--chat-overlay)', border: '0.5px solid var(--chat-border)',
          borderRadius: 12, padding: '10px 12px', marginBottom: claimError ? 4 : 8,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--chat-text)', margin: '0 0 4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{title}</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' as const }}>
            <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 99, background: 'rgba(99,102,241,0.12)', color: '#6366f1' }}>Queue</span>
            {startStr && <span style={{ fontSize: 11, color: 'var(--chat-text-dim)' }}>{startStr}</span>}
            <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 500, color: pc.text }}>{pc.label}</span>
          </div>
          {contextLabel && (
            <div style={{ marginTop: 5, fontSize: 10, color: 'var(--chat-text-dim)', display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
              <ContextTypeIcon type={contextType} />{contextLabel}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClaim(); }}
          disabled={claiming}
          style={{ flexShrink: 0, fontSize: 11, fontWeight: 600, color: 'white', background: 'var(--brand-dark, #4E38B5)', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', opacity: claiming ? 0.6 : 1, alignSelf: 'center' }}
        >
          {claiming ? '…' : 'Claim'}
        </button>
      </div>
      {claimError && (
        <p style={{ fontSize: 11, color: '#CF4148', margin: '-4px 0 8px', paddingLeft: 2 }}>
          Couldn't claim this — it may already be taken or not queued for your role.
        </p>
      )}
    </>
  );
}

// ─── MyTodosList ──────────────────────────────────────────────────────────────
// Personal items render as real TaskCard / WorkspaceMeetingCard (swipe + inline expand).
// Queue items use the compact QueueRow (Claim button, navigate-on-click).

function MyTodosList({ records, onSelectTask }: {
  records: Rec[];
  onSelectTask: (id: string) => void;
  onSelectMeeting?: (id: string) => void;
}) {
  const [claimTask, { isLoading: claiming }] = useClaimTaskMutation();
  const [claimedIds, setClaimedIds] = useState<Set<string>>(new Set());
  const [claimErrorId, setClaimErrorId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const currentUserId = useAppSelector((s) => s.sessionState.userId) ?? '';

  const personal = records.filter(r => sv(r['source']) !== 'queue');
  const queue = records.filter(r => sv(r['source']) === 'queue' && !claimedIds.has(sv(r['item_id'])));

  const handleClaim = async (taskId: string) => {
    setClaimErrorId(null);
    try {
      await claimTask(taskId).unwrap();
      setClaimedIds(prev => new Set(prev).add(taskId));
    } catch {
      setClaimErrorId(taskId);
    }
  };

  return (
    <div style={{ borderRadius: 16, padding: 12, marginTop: 4 }}>
      <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--chat-text-muted)', textTransform: 'uppercase' as const, letterSpacing: 1, marginBottom: 8, paddingLeft: 2 }}>
        my to-dos &middot; {personal.length}
      </p>

      {personal.map((r, idx) => {
        const itemId = sv(r['item_id']);
        const itemType = sv(r['item_type']);
        const ctxLabel = sv(r['context_label']);
        const ctx = ctxLabel
          ? { display_label: ctxLabel, context_type: sv(r['context_type']), entity_id: sv(r['context_entity_id']) }
          : null;

        if (itemType === 'task') {
          const task: TaskFull = {
            task_id: itemId,
            title: sv(r['title']) || 'Task',
            description: null,
            task_type: 'general',
            priority: sv(r['priority']) || 'normal',
            status: sv(r['status']),
            source: 'system',
            due_date: sv(r['due_on']) || null,
            context: ctx,
            owner_user_id: currentUserId,
            assignee_user_id: sv(r['assignee_user_id']) || null,
            queue_role_id: null,
            follower_user_ids: [],
            tags: [],
            deferred_until: null,
            active_reminder_count: r['has_reminder'] === true || String(r['has_reminder']) === 'true' ? 1 : 0,
            created_on: '', updated_on: '', created_by: null, is_following: false,
          };
          return (
            <div key={itemId || idx} style={{ marginBottom: 8 }}>
              <TaskCard task={task} expandedId={expandedId} onExpand={setExpandedId} />
            </div>
          );
        }

        const meeting: MeetingFull = {
          meeting_id: itemId,
          title: sv(r['title']) || 'Meeting',
          description: null,
          meeting_type: sv(r['meeting_type']) || 'other',
          status: sv(r['status']),
          start_time: sv(r['due_on']) || '',
          end_time: sv(r['end_time']) || '',
          location: null,
          context: ctx,
          participants: [],
          agenda: null,
          source_task_id: null,
          organizer_user_id: '',
          tags: [],
          created_on: '', updated_on: '',
        };
        return (
          <div key={itemId || idx} style={{ marginBottom: 8 }}>
            <WorkspaceMeetingCard meeting={meeting} expandedId={expandedId} onExpand={setExpandedId} />
          </div>
        );
      })}

      {personal.length === 0 && (
        <p style={{ fontSize: 12, color: 'var(--chat-text-dim)', paddingLeft: 2, marginBottom: 8 }}>No personal to-dos.</p>
      )}

      {queue.length > 0 && (
        <>
          <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--chat-text-muted)', textTransform: 'uppercase' as const, letterSpacing: 1, margin: '14px 0 8px', paddingLeft: 2 }}>
            role queue &middot; {queue.length}
          </p>
          {queue.map((r, idx) => {
            const itemId = sv(r['item_id']);
            return (
              <QueueRow
                key={itemId || idx}
                r={r}
                onNavigate={() => onSelectTask(itemId)}
                onClaim={() => { void handleClaim(itemId); }}
                claiming={claiming}
                claimError={claimErrorId === itemId}
              />
            );
          })}
        </>
      )}
    </div>
  );
}

// ─── WorkspaceTaskDetail ──────────────────────────────────────────────────────
// Fetches a task by id and renders TaskDetailPanel inside the workspace panel.

export function WorkspaceTaskDetail({ taskId, onBack, activeTab, onTabChange, onActiveTask, onOpenEngagement }: {
  taskId: string;
  onBack: () => void;
  activeTab?: TaskDetailTab;
  onTabChange?: (tab: TaskDetailTab) => void;
  /** Push the task's current fields to SAM (both agents) as active_task whenever the record
   *  changes — covers every manual edit (status, assign, follow, reminders, Save) since each one
   *  refetches it. Called with null on unmount (detail closed). */
  onActiveTask?: (task: Rec | null) => void;
  /** Navigate from a task's "Linked to: Engagement" to the engagement's property detail. */
  onOpenEngagement?: (engagementId: string) => void;
}) {
  const { data: task, isLoading, isError } = useGetTaskByIdQuery(taskId, WORKSPACE_QUERY_OPTIONS);

  // Resync-on-change: push whenever the backend record actually changes (any mutation refetches
  // it), not on every render — mirrors NoteDetailContent's lastSyncedOn pattern. The unmount-clear
  // effect uses a ref + empty deps so it fires ONLY on true unmount, not on every onActiveTask
  // identity change (e.g. a text/voice mode switch), which would otherwise wipe active_task and
  // then get skipped by the guard above (updated_on unchanged) instead of re-pushing it.
  const lastSyncedOn = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!task || task.updated_on === lastSyncedOn.current) return;
    lastSyncedOn.current = task.updated_on;
    onActiveTask?.(task as unknown as Rec);
  }, [task, onActiveTask]);
  const onActiveTaskRef = useRef(onActiveTask);
  onActiveTaskRef.current = onActiveTask;
  useEffect(() => () => onActiveTaskRef.current?.(null), []);

  return (
    <div className="flex flex-col h-full min-h-0">
      <button
        onClick={onBack}
        className="flex-shrink-0 flex items-center gap-1.5 px-1 py-2 text-xs text-brand-bright hover:opacity-80 transition-opacity"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
        Back to list
      </button>
      {isLoading && (
        <div className="flex justify-center py-12"><Spinner /></div>
      )}
      {isError && (
        <p className="text-center text-[#CF4148] py-8 text-sm">Failed to load task.</p>
      )}
      {task && (
        <div className="flex-1 overflow-y-auto no-scrollbar">
          <TaskDetailPanel task={task} activeTab={activeTab} onTabChange={onTabChange} onOpenEngagement={onOpenEngagement} />
        </div>
      )}
    </div>
  );
}

// ─── WorkspaceDirectEngagementDetail ─────────────────────────────────────────
// Fetches an engagement by id (to resolve property_id) then renders WorkspacePropertyDetail
// opened directly on that engagement. Used when the user navigates from a task's "Linked to" row.

function WorkspaceDirectEngagementDetail({ engagementId, onBack, onSelectTask, onEngagementStateChange }: {
  engagementId: string;
  onBack: () => void;
  onSelectTask?: (id: string) => void;
  onEngagementStateChange?: (state: Rec | null) => void;
}) {
  const { data: engagement, isLoading, isError } = useGetEngagementByIdQuery(engagementId);
  if (isLoading) {
    return (
      <div className="flex flex-col h-full min-h-0">
        <button onClick={onBack} className="flex-shrink-0 flex items-center gap-1.5 px-1 py-2 text-xs text-brand-bright hover:opacity-80 transition-opacity">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          Back to task
        </button>
        <div className="flex justify-center py-12"><Spinner /></div>
      </div>
    );
  }
  if (isError || !engagement?.property_id) {
    return (
      <div className="flex flex-col h-full min-h-0">
        <button onClick={onBack} className="flex-shrink-0 flex items-center gap-1.5 px-1 py-2 text-xs text-brand-bright hover:opacity-80 transition-opacity">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          Back to task
        </button>
        <p className="text-center text-[#CF4148] py-8 text-sm">Could not load engagement.</p>
      </div>
    );
  }
  return (
    <WorkspacePropertyDetail
      propertyId={engagement.property_id}
      initialEngagementId={engagementId}
      onBack={onBack}
      onSelectTask={onSelectTask}
      onEngagementStateChange={onEngagementStateChange}
    />
  );
}

// ─── WorkspaceNoteDetail ──────────────────────────────────────────────────────
// Renders the full note review card (summary / transcript / follow-ups, inline edit) inside the
// workspace panel — reuses NoteDetailContent so the SAM-driven flow and the /notes page stay one UI.

function WorkspaceNoteDetail({ noteId, onBack, onOpenTask, onNoteSynced, onNotesListChanged }: {
  noteId: string;
  onBack: () => void;
  onOpenTask?: (taskId: string) => void;
  /** Push the note's current fields to SAM (both agents) as active_note whenever the record
   *  changes or the user edits it on screen; null clears it (detail closed). */
  onNoteSynced?: (note: Rec | null) => void;
  /** A note was deleted from this detail view — refresh the on-screen notes list card (a
   *  separate cache from notesApi's own 'Note' tag) so the deleted note doesn't linger in the
   *  list underneath once "Back to list" is clicked. */
  onNotesListChanged?: () => void;
}) {
  const [deleteNote] = useDeleteNoteMutation();
  const handleDelete = async () => {
    await deleteNote(noteId).unwrap().catch(() => undefined);
    onNotesListChanged?.();
    onBack();
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <button
        onClick={onBack}
        className="flex-shrink-0 flex items-center gap-1.5 px-1 py-2 text-xs text-brand-bright hover:opacity-80 transition-opacity"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
        Back to list
      </button>
      <div className="flex-1 min-h-0">
        <NoteDetailContent noteId={noteId} onClose={onBack} onOpenTask={onOpenTask} onDelete={handleDelete} onNoteSynced={onNoteSynced} />
      </div>
    </div>
  );
}

// ─── WorkspaceMeetingDetail ───────────────────────────────────────────────────
// Fetches a meeting by id and renders MeetingDetailPanel inside the workspace panel.

export function WorkspaceMeetingDetail({ meetingId, onBack, activeTab, onActiveMeeting, onStartNoteTaker }: {
  meetingId: string;
  onBack: () => void;
  activeTab?: 'details' | 'notes' | 'activity';
  /** Push the meeting's current fields to SAM (both agents) as active_meeting whenever the
   *  record changes — covers reschedule/RSVP/complete/cancel/participants (each refetches it).
   *  Called with null on unmount (detail closed). */
  onActiveMeeting?: (meeting: Rec | null) => void;
  /** Open the Note Taker with this meeting pre-selected as context. */
  onStartNoteTaker?: (contextType: string, entityId: string) => void;
}) {
  const { data: meeting, isLoading, isError } = useGetMeetingByIdQuery(meetingId, WORKSPACE_QUERY_OPTIONS);

  // Resync-on-change — see WorkspaceTaskDetail for the identical pattern + the reasoning behind
  // the ref-based unmount-only cleanup.
  const lastSyncedOn = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!meeting || meeting.updated_on === lastSyncedOn.current) return;
    lastSyncedOn.current = meeting.updated_on;
    onActiveMeeting?.(meeting as unknown as Rec);
  }, [meeting, onActiveMeeting]);
  const onActiveMeetingRef = useRef(onActiveMeeting);
  onActiveMeetingRef.current = onActiveMeeting;
  useEffect(() => () => onActiveMeetingRef.current?.(null), []);

  return (
    <div className="flex flex-col h-full min-h-0">
      <button
        onClick={onBack}
        className="flex-shrink-0 flex items-center gap-1.5 px-1 py-2 text-xs text-brand-bright hover:opacity-80 transition-opacity"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
        Back to list
      </button>
      {isLoading && (
        <div className="flex justify-center py-12"><Spinner /></div>
      )}
      {isError && (
        <p className="text-center text-[#CF4148] py-8 text-sm">Failed to load meeting.</p>
      )}
      {meeting && (
        <div className="flex-1 overflow-y-auto no-scrollbar">
          {onStartNoteTaker && (
            <div className="px-4 pt-3 pb-1">
              <button
                type="button"
                onClick={() => onStartNoteTaker('meeting', meetingId)}
                className="flex items-center gap-2 w-full rounded-xl border border-brand-bright/40 bg-brand-bright/5 hover:bg-brand-bright/10 px-4 py-2.5 text-sm font-medium text-brand-bright transition"
              >
                <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4 flex-shrink-0" aria-hidden>
                  <path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3Z" fill="currentColor" />
                  <path d="M19 11a7 7 0 0 1-14 0M12 18v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                Start Note Taker
              </button>
            </div>
          )}
          <MeetingDetailPanel meeting={meeting} activeTab={activeTab} />
        </div>
      )}
    </div>
  );
}

// ─── WorkspacePropertyDetail ──────────────────────────────────────────────────
// Fetches a property's aggregated detail (contacts, engagements, tasks, timeline) and renders it
// inside the workspace panel — the Property CRM anchor view (CRM-FEAT-01/02/03, MVP slice).

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
}

function ContactRow({ contact, compact, extraPhoneCount, onExtraClick }: {
  contact: PropertyContact;
  compact?: boolean;
  extraPhoneCount?: number;
  /** Called when the user taps the "+N" overflow badge — e.g. to navigate to Details. */
  onExtraClick?: () => void;
}) {
  return (
    <div className={`flex items-center rounded-xl border border-ui-border bg-ui-subtle ${compact ? 'gap-2 px-2.5 py-1.5' : 'gap-3 px-3 py-2.5'}`}>
      <div className={`flex-shrink-0 rounded-full bg-brand-dark/15 text-brand-dark font-semibold flex items-center justify-center ${compact ? 'h-6 w-6 text-[10px]' : 'h-9 w-9 text-xs'}`}>
        {initials(contact.name)}
      </div>
      <div className="min-w-0 flex-1 flex items-center gap-1.5 flex-wrap">
        <span className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-ui-text`}>{contact.name}</span>
        {contact.phone && (
          <a
            href={`tel:${contact.phone}`}
            title="Call"
            className="flex-shrink-0 h-5 w-5 rounded-full bg-ui-hover hover:bg-brand-dark/10 flex items-center justify-center text-ui-text2 hover:text-brand-dark transition-colors"
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
          </a>
        )}
        {contact.phone && (
          <>
            <span className="text-ui-text3 text-xs">·</span>
            <a href={`tel:${contact.phone}`} className="text-xs text-ui-text3 hover:text-brand-dark transition-colors">{contact.phone}</a>
          </>
        )}
        {contact.role && (
          <span className="flex-shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full bg-ui-hover text-ui-text2 whitespace-nowrap">
            {humanizeLabel(contact.role)}
          </span>
        )}
        {extraPhoneCount && extraPhoneCount > 0 ? (
          onExtraClick ? (
            <button
              type="button"
              onClick={onExtraClick}
              className="flex-shrink-0 flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-brand-dark/10 text-brand-dark hover:bg-brand-dark/20 transition-colors"
              title="View all contacts in Details"
            >
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              +{extraPhoneCount}
            </button>
          ) : (
            <span className="flex-shrink-0 text-[10px] font-semibold px-2 py-1 rounded-full bg-ui-hover text-ui-text3">+{extraPhoneCount}</span>
          )
        ) : null}
      </div>
    </div>
  );
}

// ─── PropertyPhotosSection — real photo upload flow + gallery ─────────────
const MEDIA_CLASSIFICATIONS = [
  // view
  'front', 'back', 'left', 'right', 'aerial', 'interior',
  // area
  'roof', 'gutters', 'flashing', 'chimney', 'vents', 'skylight', 'attic', 'yard',
  // stage
  'before', 'during', 'after', 'damage', 'warranty_evidence',
];

function PropertyPhotosSection({ propertyId, engagementId, media: allMedia, readOnly }: {
  propertyId: string;
  engagementId?: string;
  media: Array<{ media_id: string; media_type: string; s3_url: string; filename?: string | null; classifications: string[]; caption?: string | null }>;
  /** Media tab renders read-only (aggregated view across all engagements + property); uploads
   *  only happen from inside an opened engagement, tagged with that engagement_id. */
  readOnly?: boolean;
}) {
  // The same /properties/{id}/media list now carries photo, video AND file records; the
  // Files section renders the non-visual ones separately, so gate the gallery here.
  const media = allMedia.filter(m => m.media_type === 'photo' || m.media_type === 'video');
  const [modalOpen, setModalOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [classifications, setClassifications] = useState<string[]>([]);
  const [caption, setCaption] = useState('');
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadFile, { isLoading: uploading }] = useUploadMediaFileMutation();
  const [attach, { isLoading: attaching }] = useAttachPropertyMediaMutation();

  const canSubmit = !!file && classifications.length > 0 && !uploading && !attaching;

  async function submit() {
    if (!file) return;
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const uploaded = await uploadFile(fd).unwrap();
      await attach({
        propertyId,
        body: {
          engagement_id: engagementId,
          s3_url: uploaded.url,
          media_type: file.type.startsWith('video/') ? 'video' : 'photo',
          filename: uploaded.filename,
          classifications,
          caption: caption.trim() || undefined,
        },
      }).unwrap();
      setModalOpen(false);
      setFile(null);
      setClassifications([]);
      setCaption('');
    } catch (e) {
      setError('Upload failed — please try again.');
    }
  }

  return (
    <>
      <CardSection
        title="Photos"
        count={media.length}
        onAdd={readOnly ? undefined : () => setModalOpen(true)}
        addLabel="Add Photo"
        emptyText={
          media.length === 0
            ? readOnly
              ? "No photos yet. Add them from inside an engagement."
              : "No photos yet. Add before, during, and after shots — future crews will thank you."
            : undefined
        }
      >
        {media.length > 0 && (
          <>
            <div className="grid grid-cols-3 gap-2">
              {media.slice(0, 6).map(m => (
                <a
                  key={m.media_id}
                  href={m.s3_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group aspect-square rounded-lg overflow-hidden border border-ui-border bg-ui-subtle relative flex items-center justify-center"
                  title={m.caption || m.filename || ''}
                >
                  {m.media_type === 'video' ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-ui-text3"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
                  ) : (
                    <img src={m.s3_url} alt={m.caption || m.filename || ''} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  )}
                  {m.classifications.length > 0 && (
                    <div className="absolute bottom-1 left-1 right-1 flex flex-wrap gap-1">
                      {m.classifications.slice(0, 2).map(cls => (
                        <span key={cls} className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-black/60 text-white truncate max-w-full">
                          {humanizeLabel(cls)}
                        </span>
                      ))}
                    </div>
                  )}
                </a>
              ))}
            </div>
            {media.length > 6 && <p className="text-[11px] text-ui-text3">+{media.length - 6} more</p>}
          </>
        )}
      </CardSection>

      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setModalOpen(false)}>
          <div className="w-full max-w-md rounded-2xl bg-ui-card border border-ui-border p-5 flex flex-col gap-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-ui-text">Add a photo</h3>
              <button type="button" onClick={() => setModalOpen(false)} className="text-ui-text3 hover:text-ui-text">✕</button>
            </div>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-ui-text2">Photo or short video</span>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/mp4,video/quicktime,video/webm"
                onChange={e => setFile(e.target.files?.[0] || null)}
                className="text-xs file:mr-3 file:px-3 file:py-1.5 file:rounded-full file:border-0 file:bg-brand-dark/10 file:text-brand-dark file:font-medium hover:file:bg-brand-dark/15"
              />
              {file && <span className="text-[11px] text-ui-text3">{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</span>}
            </label>
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-ui-text2">Tag it (pick one or more)</span>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto no-scrollbar">
                {MEDIA_CLASSIFICATIONS.map(cls => {
                  const active = classifications.includes(cls);
                  return (
                    <button
                      key={cls}
                      type="button"
                      onClick={() => setClassifications(prev => active ? prev.filter(c => c !== cls) : [...prev, cls])}
                      className={`text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors ${
                        active
                          ? 'bg-brand-dark text-white border-brand-dark'
                          : 'bg-ui-subtle text-ui-text2 border-ui-border hover:bg-ui-hover'
                      }`}
                    >
                      {humanizeLabel(cls)}
                    </button>
                  );
                })}
              </div>
            </div>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-ui-text2">Caption (optional)</span>
              <input
                type="text"
                value={caption}
                onChange={e => setCaption(e.target.value)}
                placeholder="e.g. Impact damage on north slope after 2026-06 storm"
                className="rounded-lg border border-ui-border bg-ui-subtle px-3 py-2 text-sm focus:outline-none focus:border-brand-dark"
              />
            </label>
            {error && <p className="text-xs text-red-600">{error}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setModalOpen(false)} className="text-xs font-medium px-3 py-1.5 rounded-full border border-ui-border text-ui-text2 hover:bg-ui-hover">Cancel</button>
              <button
                type="button"
                disabled={!canSubmit}
                onClick={submit}
                className="text-xs font-medium px-3 py-1.5 rounded-full bg-brand-dark text-white disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90"
              >
                {uploading || attaching ? 'Uploading…' : 'Upload photo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── PropertyFilesSection — non-image/video attachments (PDFs, docs, spreadsheets) ─────
const FILE_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv,application/zip';

function fileIconFor(name?: string | null): string {
  const ext = (name || '').split('.').pop()?.toLowerCase() || '';
  if (ext === 'pdf') return '📕';
  if (['doc', 'docx'].includes(ext)) return '📄';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return '📊';
  if (['ppt', 'pptx'].includes(ext)) return '📽️';
  if (ext === 'zip') return '🗜️';
  return '📎';
}

function PropertyFilesSection({ propertyId, engagementId, media: allMedia, readOnly }: {
  propertyId: string;
  engagementId?: string;
  media: Array<{ media_id: string; media_type: string; s3_url: string; filename?: string | null; classifications: string[]; caption?: string | null }>;
  readOnly?: boolean;
}) {
  const files = allMedia.filter(m => m.media_type === 'file');
  const [modalOpen, setModalOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [caption, setCaption] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [uploadFile, { isLoading: uploading }] = useUploadMediaFileMutation();
  const [attach, { isLoading: attaching }] = useAttachPropertyMediaMutation();

  async function submit() {
    if (!file) return;
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const uploaded = await uploadFile(fd).unwrap();
      await attach({
        propertyId,
        body: {
          engagement_id: engagementId,
          s3_url: uploaded.url,
          media_type: 'file',
          filename: uploaded.filename,
          caption: caption.trim() || undefined,
        },
      }).unwrap();
      setModalOpen(false);
      setFile(null);
      setCaption('');
    } catch {
      setError('Upload failed — please try again.');
    }
  }

  return (
    <>
      <CardSection
        title="Files"
        count={files.length}
        onAdd={readOnly ? undefined : () => setModalOpen(true)}
        addLabel="Add File"
        emptyText={files.length === 0
          ? readOnly ? "No files yet. Add them from inside an engagement."
                     : "No files yet. Drop in a signed contract, permit, or inspection report."
          : undefined}
      >
        {files.length > 0 && (
          <div className="flex flex-col gap-1.5">
            {files.map(f => (
              <a
                key={f.media_id}
                href={f.s3_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 rounded-lg border border-ui-border bg-ui-subtle px-3 py-2 hover:bg-ui-hover transition-colors"
              >
                <span className="text-lg flex-shrink-0" aria-hidden="true">{fileIconFor(f.filename)}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-ui-text truncate">{f.filename || 'File'}</p>
                  {f.caption && <p className="text-[11px] text-ui-text3 truncate">{f.caption}</p>}
                </div>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0 text-ui-text3">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              </a>
            ))}
          </div>
        )}
      </CardSection>

      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setModalOpen(false)}>
          <div className="w-full max-w-md rounded-2xl bg-ui-card border border-ui-border p-5 flex flex-col gap-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-ui-text">Add a file</h3>
              <button type="button" onClick={() => setModalOpen(false)} className="text-ui-text3 hover:text-ui-text">✕</button>
            </div>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-ui-text2">Document</span>
              <input
                type="file"
                accept={FILE_ACCEPT}
                onChange={e => setFile(e.target.files?.[0] || null)}
                className="text-xs file:mr-3 file:px-3 file:py-1.5 file:rounded-full file:border-0 file:bg-brand-dark/10 file:text-brand-dark file:font-medium hover:file:bg-brand-dark/15"
              />
              {file && <span className="text-[11px] text-ui-text3">{file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB</span>}
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-ui-text2">Caption (optional)</span>
              <input
                type="text"
                value={caption}
                onChange={e => setCaption(e.target.value)}
                placeholder="e.g. Signed material approval, GAF Timberline HDZ"
                className="rounded-lg border border-ui-border bg-ui-subtle px-3 py-2 text-sm focus:outline-none focus:border-brand-dark"
              />
            </label>
            {error && <p className="text-xs text-red-600">{error}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setModalOpen(false)} className="text-xs font-medium px-3 py-1.5 rounded-full border border-ui-border text-ui-text2 hover:bg-ui-hover">Cancel</button>
              <button
                type="button"
                disabled={!file || uploading || attaching}
                onClick={submit}
                className="text-xs font-medium px-3 py-1.5 rounded-full bg-brand-dark text-white disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90"
              >
                {uploading || attaching ? 'Uploading…' : 'Upload file'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── PropertyNotesSection — inline mini-form for text notes ─────────────
// 'measurement'/'inspection'/'site_observation' (VIN engagement-work taxonomy) are included
// alongside the PCR property-context types — without them there was no note_type a user could
// pick here that satisfies the assessment_note_required governance gate (required_note gate on
// complete_assessment checks note_type in these three, or context_type/tags — neither of which
// this simple form sets), so "Complete Assessment" was unsatisfiable from this panel.
const NOTE_TYPES = ['measurement', 'inspection', 'site_observation', 'access', 'safety', 'special_instruction', 'customer_preference', 'general'];

function PropertyNoteRow({ note }: {
  note: { note_id: string; title?: string | null; note_type: string; source?: string | null; audio_url?: string | null; final_transcript?: string | null; clean_transcript?: string | null; review_summary?: string | null; created_on?: string | null };
}) {
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const previewRef = useRef<HTMLParagraphElement>(null);
  const transcript = note.clean_transcript || note.final_transcript;
  const isVoice = note.source === 'voice' || !!note.audio_url;
  const preview = note.review_summary || transcript || note.title || '(empty note)';

  useEffect(() => {
    if (previewRef.current) {
      const el = previewRef.current;
      setIsClamped(el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth);
    }
  }, [preview]);

  return (
    <div className="rounded-lg border border-ui-border bg-ui-subtle px-3 py-2">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide bg-brand-dark/10 text-brand-dark">
          {humanizeLabel(note.note_type)}
        </span>
        {isVoice && (
          <span
            className="flex-shrink-0 flex items-center gap-1 text-[10px] font-medium text-ui-text2"
            title="Voice note"
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
            Voice
          </span>
        )}
        {note.created_on && <span className="text-[10px] text-ui-text3 ml-auto">{fmtDate(note.created_on)}</span>}
      </div>
      <p ref={previewRef} className={`text-xs text-ui-text2 break-all${expanded ? ' whitespace-pre-wrap' : ' line-clamp-1'}`}>
        {preview}
      </p>
      {note.audio_url && (
        <div className="mt-2">
          <audio controls preload="none" src={note.audio_url} className="h-8 w-full">
            Your browser does not support audio playback.
          </audio>
        </div>
      )}
      {(isClamped || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="mt-1 text-[10px] font-medium text-brand-dark hover:opacity-80"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

function PropertyNotesSection({ propertyId, engagementId, notes, readOnly }: {
  propertyId: string;
  engagementId?: string;
  notes: Array<{ note_id: string; title?: string | null; note_type: string; source?: string | null; audio_url?: string | null; final_transcript?: string | null; clean_transcript?: string | null; review_summary?: string | null; created_on?: string | null }>;
  readOnly?: boolean;
}) {
  const [formOpen, setFormOpen] = useState(false);
  const [text, setText] = useState('');
  const [noteType, setNoteType] = useState('access');
  const [error, setError] = useState<string | null>(null);
  const [createNote, { isLoading }] = useCreatePropertyNoteMutation();

  async function submit() {
    if (!text.trim()) return;
    setError(null);
    try {
      await createNote({
        propertyId,
        body: {
          text_content: text.trim(),
          note_type: noteType,
          title: text.slice(0, 60),
          engagement_id: engagementId,
        },
      }).unwrap();
      setText('');
      setFormOpen(false);
    } catch {
      setError('Could not save the note — try again.');
    }
  }

  return (
    <CardSection
      title="Notes"
      count={notes.length}
      onAdd={readOnly ? undefined : () => setFormOpen(v => !v)}
      addLabel="Add Note"
      emptyText={notes.length === 0 && !formOpen
        ? readOnly ? "No notes yet. Add them from inside an engagement."
                   : "Gate codes, dog warnings, access instructions — save them here so every crew sees them."
        : undefined}
    >
      {formOpen && (
        <div className="rounded-lg border border-ui-border bg-ui-card p-3 flex flex-col gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {NOTE_TYPES.map(nt => (
              <button
                key={nt}
                type="button"
                onClick={() => setNoteType(nt)}
                className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${noteType === nt ? 'bg-brand-dark text-white' : 'bg-ui-subtle text-ui-text2 border border-ui-border'}`}
              >
                {humanizeLabel(nt)}
              </button>
            ))}
          </div>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="e.g. Gate code is 4321. Dog in backyard."
            rows={2}
            className="rounded-lg border border-ui-border bg-ui-subtle px-3 py-2 text-sm resize-none focus:outline-none focus:border-brand-dark"
          />
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setFormOpen(false)} className="text-xs px-3 py-1 rounded-full text-ui-text2 hover:bg-ui-hover">Cancel</button>
            <button type="button" disabled={!text.trim() || isLoading} onClick={submit} className="text-xs font-medium px-3 py-1 rounded-full bg-brand-dark text-white disabled:opacity-40 hover:opacity-90">
              {isLoading ? 'Saving…' : 'Save note'}
            </button>
          </div>
        </div>
      )}
      {notes.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {notes.slice(0, 5).map(n => (
            <PropertyNoteRow key={n.note_id} note={n} />
          ))}
          {notes.length > 5 && <p className="text-[11px] text-ui-text3">+{notes.length - 5} more</p>}
        </div>
      )}
    </CardSection>
  );
}

// ─── EngagementActivitySection ───────────────────────────────────
// Base config — dot colour may be overridden at render time based on detail.status
const ACTIVITY_CFG: Record<string, { label: string; dot: string }> = {
  engagement_created:          { label: 'Engagement created',       dot: 'bg-brand-dark' },
  stage_advanced:              { label: 'Stage advanced',            dot: 'bg-success' },
  status_changed:              { label: 'Status moved',              dot: 'bg-brand-dark' },
  transition_executed:         { label: 'Action taken',             dot: 'bg-success' },
  hook_executed:               { label: 'Automation ran',           dot: 'bg-success' },   // overridden if status=failed
  governance_action_executed:  { label: 'Gate action executed',     dot: 'bg-success' },   // overridden if status=failed
  note_added:                  { label: 'Note added',               dot: 'bg-brand-dark' },
  call_logged:                 { label: 'Call logged',              dot: 'bg-brand-dark' },
  assessment_completed:        { label: 'Assessment completed',     dot: 'bg-success' },
  put_on_hold:                 { label: 'Put on hold',              dot: 'bg-amber-500' },
  resumed:                     { label: 'Resumed',                  dot: 'bg-success' },
  closed_early:                { label: 'Closed early',             dot: 'bg-red-500' },
  holder_reassigned:           { label: 'Holder reassigned',        dot: 'bg-brand-dark' },
  access_policy_updated:       { label: 'Access policy updated',    dot: 'bg-ui-text3' },
  task_created:                { label: 'Task created',             dot: 'bg-brand-dark' },
  task_completed:              { label: 'Task completed',           dot: 'bg-success' },
  approval_requested:          { label: 'Approval requested',       dot: 'bg-amber-500' },
  approval_decided:            { label: 'Approval decided',         dot: 'bg-success' },
};

// Maps backend hook_code / action_type to a human-friendly label.
function hookLabel(hookCode: string | null | undefined, actionType: string | null | undefined): string {
  if (actionType === 'task_creation') return 'Task auto-created';
  if (actionType === 'approval_creation') return 'Approval auto-created';
  if (actionType === 'notification') return 'Notification sent';
  if (actionType === 'module_action') return 'Module action triggered';
  if (hookCode) return humanizeLabel(hookCode.replace(/_/g, ' '));
  if (actionType) return humanizeLabel(String(actionType).replace(/_/g, ' '));
  return 'Automation ran';
}

function activityDetail(a: EngagementActivity): string | null {
  const d = a.detail;
  if (!d) return null;
  const s = (v: unknown) => String(v);
  switch (a.activity_type) {
    case 'stage_advanced':
      if (d.from_stage && d.to_stage) return `${s(d.from_stage)} → ${s(d.to_stage)}`;
      if (d.to_stage) return `Moved to ${s(d.to_stage)}`;
      break;
    case 'status_changed':
      if (d.old_status && d.new_status) return `${humanizeLabel(s(d.old_status))} → ${humanizeLabel(s(d.new_status))}`;
      if (d.from_status && d.to_status) return `${humanizeLabel(s(d.from_status))} → ${humanizeLabel(s(d.to_status))}`;
      break;
    case 'engagement_created': {
      const displayLabels = d.display_labels as Record<string, unknown> | undefined;
      if (displayLabels?.status) return `Started at ${s(displayLabels.status)}`;
      if (d.initial_status) return `Started at ${humanizeLabel(s(d.initial_status))}`;
      break;
    }
    case 'transition_executed': {
      const actionLabel = d.action_label ? s(d.action_label) : (d.transition_code ? humanizeLabel(s(d.transition_code)) : null);
      const fromLabel = d.from_status_label ? s(d.from_status_label) : null;
      const toLabel = d.to_status_label ? s(d.to_status_label) : null;
      if (fromLabel && toLabel) return `${actionLabel ? actionLabel + ' · ' : ''}${fromLabel} → ${toLabel}`;
      if (actionLabel) return actionLabel;
      break;
    }
    case 'hook_executed': {
      const hookCode = d.hook_code as string | undefined;
      const actionType = d.action_type as string | undefined;
      const triggerRef = d.trigger_ref as string | undefined;
      const status = d.status as string | undefined;
      if (status === 'failed' && d.error_detail) return `Failed: ${s(d.error_detail)}`;
      const base = hookLabel(hookCode, actionType);
      // Append trigger context for clarity, e.g. "Task auto-created on entering New Prospect"
      if (triggerRef) return `${base} · ${humanizeLabel(s(triggerRef))}`;
      return base;
    }
    case 'governance_action_executed': {
      const gate = d.gate_code as string | undefined;
      const action = d.action_key as string | undefined;
      const status = d.status as string | undefined;
      if (status === 'failed' && d.error_detail) return `Failed: ${s(d.error_detail)}`;
      if (gate && action) return `${humanizeLabel(s(gate))} — ${humanizeLabel(s(action))}`;
      if (gate) return humanizeLabel(s(gate));
      break;
    }
    case 'put_on_hold':
      if (d.reason) return s(d.reason);
      break;
    case 'closed_early':
      if (d.reason) return s(d.reason);
      if (d.closure_type) return humanizeLabel(s(d.closure_type));
      break;
    case 'holder_reassigned':
      if (d.new_holder_name) return `Reassigned to ${s(d.new_holder_name)}`;
      break;
    case 'approval_decided':
      if (d.decision && d.comments) return `${humanizeLabel(s(d.decision))} — ${s(d.comments)}`;
      if (d.decision) return humanizeLabel(s(d.decision));
      break;
  }
  if (d.description) return s(d.description);
  return null;
}

// Dynamic dot colour: success/failure hooks override the base config colour.
function activityDotColor(a: EngagementActivity, baseDot: string): string {
  if (a.activity_type === 'hook_executed' || a.activity_type === 'governance_action_executed') {
    const status = a.detail?.status as string | undefined;
    if (status === 'success') return 'bg-success';
    if (status === 'failed') return 'bg-red-500';
  }
  return baseDot;
}

function relTimeShort(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

const ACTIVITY_PREVIEW = 5;

function EngagementActivitySection({ engagementId }: { engagementId: string }) {
  // refetchOnMountOrArgChange: true — always fetch fresh data when this panel is opened/navigated to.
  const { data: items = [], isLoading } = useGetEngagementActivityQuery(engagementId, { refetchOnMountOrArgChange: true });
  const [showAll, setShowAll] = React.useState(false);
  // Sort descending by occurred_at so the latest activity is always at the top.
  const sorted = [...items].sort(
    (a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime()
  );
  const visible = showAll ? sorted : sorted.slice(0, ACTIVITY_PREVIEW);
  const hasMore = sorted.length > ACTIVITY_PREVIEW;

  return (
    <CardSection
      title="Activity"
      count={items.length}
      emptyText={!isLoading && items.length === 0 ? 'No activity recorded yet.' : undefined}
    >
      {isLoading ? (
        <div className="flex justify-center py-2"><Spinner size="sm" /></div>
      ) : sorted.length > 0 ? (
        <div className="relative">
          {/* Vertical connector line */}
          <div className="absolute left-[5px] top-2 bottom-2 w-px bg-ui-border" />
          <div className="flex flex-col gap-3">
            {visible.map((a, i) => {
              const cfg = ACTIVITY_CFG[a.activity_type];
              // For hook/automation events use the richer hookLabel as the main entry title.
              const label = (a.activity_type === 'hook_executed')
                ? hookLabel(a.detail?.hook_code as string | undefined, a.detail?.action_type as string | undefined)
                : (cfg?.label ?? humanizeLabel(a.activity_type));
              const dot = activityDotColor(a, cfg?.dot ?? 'bg-ui-text3');
              const desc = activityDetail(a);
              // actor_name is now populated for hook events after the backend fix;
              // fall back to "System" for automated/internal events.
              const actor = a.actor_name ?? 'System';
              const failed = (a.activity_type === 'hook_executed' || a.activity_type === 'governance_action_executed')
                && (a.detail?.status as string | undefined) === 'failed';
              return (
                <div key={i} className="flex items-start gap-3 relative">
                  <span className={`mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full ${dot} ring-2 ring-ui-card z-10`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className={`text-xs font-medium ${failed ? 'text-red-600 dark:text-red-400' : 'text-ui-text'}`}>{label}</span>
                      <span className="flex-shrink-0 text-[10px] text-ui-text3">{relTimeShort(a.occurred_at)}</span>
                    </div>
                    {desc && (
                      <p className="mt-0.5 text-[11px] text-ui-text2 leading-snug">{desc}</p>
                    )}
                    <p className="mt-0.5 text-[10px] text-ui-text3">{actor}</p>
                  </div>
                </div>
              );
            })}
          </div>
          {hasMore && (
            <button
              onClick={() => setShowAll(v => !v)}
              className="mt-3 ml-[18px] text-[11px] text-brand font-medium hover:underline"
            >
              {showAll ? 'Show less' : `View ${sorted.length - ACTIVITY_PREVIEW} more`}
            </button>
          )}
        </div>
      ) : null}
    </CardSection>
  );
}

// ─── PropertyMeasurementsSection — inline mini-form ─────────────
function PropertyMeasurementsSection({ propertyId, engagementId, currentMeasurement, measurements, readOnly }: {
  propertyId: string;
  engagementId?: string;
  currentMeasurement?: { squares?: number | null; pitch?: string | null; provider?: string | null; how_obtained: string; notes?: string | null } | null;
  measurements: Array<{
    measurement_id: string; how_obtained: string; provider?: string | null; measured_on?: string | null;
    is_current_reference: boolean;
    values: { squares?: number | null; pitch?: string | null; ridge_length_ft?: number | null; total_area_sqft?: number | null };
    report_url?: string | null; report_filename?: string | null; notes?: string | null;
  }>;
  readOnly?: boolean;
}) {
  const [formOpen, setFormOpen] = useState(false);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [createM, { isLoading }] = useCreatePropertyMeasurementMutation();

  async function submit() {
    setError(null);
    const trimmed = text.trim();
    if (!trimmed) { setError('Enter the measurement details.'); return; }
    try {
      await createM({
        propertyId,
        body: {
          source_engagement_id: engagementId,
          how_obtained: 'manual',
          notes: trimmed,
          set_as_current_reference: true,
        },
      }).unwrap();
      setText('');
      setFormOpen(false);
    } catch {
      setError('Could not save the measurement — try again.');
    }
  }

  return (
    <>
      {currentMeasurement && !formOpen && (
        <div className="rounded-xl border border-ui-border bg-ui-card px-3 py-3">
          <div className="flex items-center justify-between mb-1.5">
            <h3 className="text-sm font-semibold text-ui-text">Measurements</h3>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-medium text-ui-text3 uppercase tracking-wide">{measurements.length} set{measurements.length === 1 ? '' : 's'} on file</span>
              {!readOnly && (
                <button type="button" onClick={() => setFormOpen(true)} className="text-xs font-medium px-2.5 py-1 rounded-full bg-brand-dark/10 text-brand-dark hover:bg-brand-dark/15 transition-colors">+ Add</button>
              )}
            </div>
          </div>
          <div className="text-xs text-ui-text2 flex items-center gap-2 flex-wrap">
            {(currentMeasurement.squares != null || currentMeasurement.pitch) ? (
              <>
                <span className="font-semibold text-ui-text">{currentMeasurement.squares ?? '—'} squares</span>
                <span>·</span>
                <span>pitch {currentMeasurement.pitch || '—'}</span>
              </>
            ) : currentMeasurement.notes ? (
              <span className="text-ui-text truncate">{currentMeasurement.notes}</span>
            ) : (
              <span className="text-ui-text3">No details recorded</span>
            )}
            {currentMeasurement.provider && (<><span>·</span><span className="text-ui-text3">{currentMeasurement.provider} ({humanizeLabel(currentMeasurement.how_obtained)})</span></>)}
          </div>
          {/* All sets on file — each row exposes its source report (EagleView PDF etc.) as a
              download link, so a contractor can pull the original artifact for the next job.
              The `is_current_reference` set is called out with a tag; older sets stay accessible
              per FR-03-017 (non-destructive supersession). */}
          {measurements.length > 0 && (
            <div className="mt-2.5 pt-2 border-t border-ui-border flex flex-col gap-1.5">
              {measurements.map(m => (
                <div key={m.measurement_id} className="flex items-center gap-2 text-[11px]">
                  {m.is_current_reference && (
                    <span className="flex-shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide bg-brand-dark/10 text-brand-dark">Current</span>
                  )}
                  <span className="text-ui-text2 truncate flex-1">
                    {(m.values.squares != null || m.values.pitch) ? (
                      <>
                        <span className="font-medium text-ui-text">{m.values.squares ?? '—'} sq</span>
                        {m.values.pitch && <> · {m.values.pitch}</>}
                      </>
                    ) : m.notes ? (
                      <span className="font-medium text-ui-text">{m.notes}</span>
                    ) : null}
                    {m.provider && <> · {m.provider}</>}
                    <span className="text-ui-text3"> ({humanizeLabel(m.how_obtained)})</span>
                    {m.measured_on && <span className="text-ui-text3"> · {fmtDate(m.measured_on)}</span>}
                  </span>
                  {m.report_url ? (
                    <a
                      href={m.report_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0 flex items-center gap-1 text-brand-dark hover:opacity-80 font-medium"
                      title={m.report_filename || 'Open report'}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      Report
                    </a>
                  ) : (
                    <span className="flex-shrink-0 text-ui-text3">—</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {(!currentMeasurement || formOpen) && (
        <CardSection
          title="Measurements"
          onAdd={!readOnly && !formOpen ? () => setFormOpen(true) : undefined}
          addLabel="Add Measurement"
          emptyText={!formOpen && !currentMeasurement
            ? readOnly ? "No measurements yet. Add them from inside an engagement."
                       : "No measurements yet. Describe them in your own words — squares, pitch, dimensions — or paste them from a report."
            : undefined}
        >
          {formOpen && (
            <div className="rounded-lg border border-ui-border bg-ui-card p-3 flex flex-col gap-2">
              <label className="flex flex-col gap-1">
                <span className="text-[10px] text-ui-text3 uppercase tracking-wide">Measurement details</span>
                <textarea
                  value={text}
                  onChange={e => setText(e.target.value)}
                  rows={4}
                  placeholder="e.g. 24.5 squares, 6/12 pitch, 42 ft ridge — or paste from a report"
                  className="rounded-lg border border-ui-border bg-ui-subtle px-2 py-1.5 text-sm focus:outline-none focus:border-brand-dark resize-y"
                />
              </label>
              {error && <p className="text-xs text-red-600">{error}</p>}
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setFormOpen(false)} className="text-xs px-3 py-1 rounded-full text-ui-text2 hover:bg-ui-hover">Cancel</button>
                <button type="button" disabled={isLoading} onClick={submit} className="text-xs font-medium px-3 py-1 rounded-full bg-brand-dark text-white disabled:opacity-40 hover:opacity-90">
                  {isLoading ? 'Saving…' : 'Save measurement'}
                </button>
              </div>
            </div>
          )}
        </CardSection>
      )}
    </>
  );
}

export function WorkspacePropertyDetail({ propertyId, onBack, onSelectTask, initialEngagementId, hideBackButton, engagementRev, onEngagementStateChange }: {
  propertyId: string;
  onBack: () => void;
  /** Opens a task detail from the engagement's Tasks section (workflow-created tasks). */
  onSelectTask?: (id: string) => void;
  /** Reached from the Engagements list ("open the first one") — go straight to that
   * engagement's WorkflowCard instead of the tab's own multi-engagement picker list. */
  initialEngagementId?: string | null;
  /** No "list" to return to when this card is the top-level surface (e.g. reached directly
   *  from a SAM workflow tool call rather than from the Properties/Engagements list). */
  hideBackButton?: boolean;
  /** Bumped whenever SAM re-opens/re-points this surface at initialEngagementId — forces the
   *  embedded WorkflowCard to refetch even when nothing invalidated its RTK Query cache. */
  engagementRev?: number;
  /** Pushes the embedded engagement's live workflow state up to SAM (active_engagement_workflow
   *  context) — see root CLAUDE.md's UI context pipeline. Only wired when this detail card is
   *  reached directly from a workflow tool call; the Properties-list path doesn't push it. */
  onEngagementStateChange?: (state: Rec | null) => void;
}) {
  const { data: property, isLoading, isError } = useGetPropertyDetailQuery(propertyId, WORKSPACE_QUERY_OPTIONS);
  // PCR-FEAT-03 Property Profile sub-lists — parallel fetches so the detail streams in as
  // pieces arrive. Each has its own RTK Query cache tag so SAM tool actions targeted at a
  // specific section (attach_property_media, create_warranty, create_measurement,
  // create_property_note) invalidate just that section.
  const { data: media = [] } = useGetPropertyMediaQuery(propertyId, WORKSPACE_QUERY_OPTIONS);
  const { data: measurements = [] } = useGetPropertyMeasurementsQuery(propertyId, WORKSPACE_QUERY_OPTIONS);
  const { data: propNotes = [] } = useGetPropertyNotesQuery(propertyId, WORKSPACE_QUERY_OPTIONS);
  const { data: propertyContacts = [] } = useGetPropertyContactsQuery(propertyId, WORKSPACE_QUERY_OPTIONS);
  // Default to Engagement — this card is reached via the property/engagements list, where the
  // engagement (its stage/status) is what the user just came in to look at; Media starts empty
  // until something's uploaded, so opening there first is a wasted extra tap.
  const [tab, setTab] = useState<'media' | 'engagement'>('engagement');
  // When there are multiple engagements, the tab shows the minimal card list until one is
  // picked; single-engagement properties open straight into that engagement's WorkflowCard.
  // initialEngagementId seeds this so arriving from the Engagements list opens the specific
  // engagement that was clicked, not just "the only one" or the picker list.
  const [openEngagementId, setOpenEngagementId] = useState<string | null>(initialEngagementId ?? null);
  // Set to true when the user explicitly clicks "Back to engagements" so the useEffect below
  // doesn't immediately re-apply initialEngagementId and trap them in a loop. Reset whenever
  // initialEngagementId itself changes (SAM re-pointing to a different engagement).
  const userBackedRef = useRef(false);
  useEffect(() => { userBackedRef.current = false; }, [initialEngagementId]);
  // Holds the ID of a newly created engagement until the list re-fetches and confirms it
  const pendingNewEngagementRef = useRef<string | null>(null);
  const [engagementDropdownOpen, setEngagementDropdownOpen] = useState(false);
  const engagementDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!engagementDropdownOpen) return;
    function handleClick(e: MouseEvent) {
      if (engagementDropdownRef.current && !engagementDropdownRef.current.contains(e.target as Node)) {
        setEngagementDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [engagementDropdownOpen]);
  const [addressExpanded, setAddressExpanded] = useState(false);

  // Prefer the Contacts-module primary POC (fresh, comes from property_contacts via
  // primary_contact) over the legacy embedded `contacts[]` array — same precedence as
  // WorkspacePropertyCard's owner display, and for the same reason: contacts added through
  // link_contact_to_property never land in `contacts[]`, so relying on that array alone
  // silently misses any contact added the new way.
  const primaryContactSummary = property?.primary_contact;
  const primary: PropertyContact | undefined = primaryContactSummary
    ? {
        name: primaryContactSummary.display_name,
        role: primaryContactSummary.relationship_type || 'owner',
        is_primary: true,
        phone: primaryContactSummary.phone,
        email: primaryContactSummary.email,
      }
    : property?.contacts.find(c => c.is_primary) || property?.contacts[0];

  // Header shows only the primary POC — contact_summary.primary_poc is the freshest source
  // (refreshed by the contacts-module worker whenever the contact list changes); extras are
  // shown as "+N" so the header stays compact. Full contact list is accessible via Details tab.
  const pocSummary = property?.contact_summary?.primary_poc;
  const headerPoc: PropertyContact | undefined = pocSummary
    ? {
        name: pocSummary.display_name,
        // Use the real relationship type from primary_contact; suppress 'primary_poc' label.
        role: property?.primary_contact?.relationship_type || '',
        is_primary: true,
        phone: pocSummary.phone ?? null,
        email: pocSummary.email ?? null,
      }
    : primary;
  const totalContactCount = property?.contact_summary?.total_contacts ?? 1;
  const extraContactCount = Math.max(0, totalContactCount - 1);

  const engagements = property?.engagements ?? [];
  // Auto-open the only engagement so the tab lands on the card, not a one-item list; also seeds/
  // re-syncs openEngagementId from initialEngagementId — needed because (a) this component
  // doesn't remount when SAM re-points an already-open surface at a different engagement/property
  // (propertyId and initialEngagementId are just props, not keys), and (b) when propertyId itself
  // just changed, `engagements` is briefly [] while this property's own query is in flight — must
  // wait rather than clear, or this effect's own "clear stale selection" branch below would wipe
  // out the freshly-requested target before its property's data ever arrives to confirm it.
  useEffect(() => {
    if (tab !== 'engagement') return;
    // If the user just created a new engagement via "+", select it once the list contains it —
    // checked before initialEngagementId below, since clicking "+" is a more recent, explicit
    // user action than whatever engagement SAM last pointed this surface at. Otherwise the
    // initialEngagementId branch keeps winning (it's still valid/present) and the freshly
    // created engagement never gets selected even though it was created successfully.
    if (pendingNewEngagementRef.current) {
      if (engagements.some(e => e.engagement_id === pendingNewEngagementRef.current)) {
        setOpenEngagementId(pendingNewEngagementRef.current);
        pendingNewEngagementRef.current = null;
        return;
      }
      return; // still waiting for re-fetch to include the new engagement — don't clear
    }
    if (initialEngagementId && !userBackedRef.current) {
      if (engagements.some(e => e.engagement_id === initialEngagementId)) {
        if (openEngagementId !== initialEngagementId) setOpenEngagementId(initialEngagementId);
        return;
      }
      if (!engagements.length) return; // this property's engagements haven't loaded yet — wait
    }
    // Auto-open the first engagement even for multi-engagement properties — the dropdown lets
    // the user switch, so there's no reason to show a list first.
    if (engagements.length >= 1 && !openEngagementId && !userBackedRef.current) setOpenEngagementId(engagements[0].engagement_id);
    if (engagements.length >= 1 && openEngagementId && !engagements.some(e => e.engagement_id === openEngagementId)) {
      setOpenEngagementId(null);
    }
  }, [tab, engagements, openEngagementId, initialEngagementId]);

  return (
    <div className="flex flex-col h-full min-h-0">
      {isLoading && (
        <div className="flex justify-center py-12"><Spinner /></div>
      )}
      {isError && (
        <p className="text-center text-[#CF4148] py-8 text-sm">Failed to load property.</p>
      )}
      {property && (
        <div className="flex-1 min-h-0 flex flex-col">
          {/* Header — only property address pinned here; contact + engagement row scroll below */}
          <div className="flex-shrink-0 px-1 pt-1 pb-1.5 border-b border-ui-border">
            <div className="flex items-start gap-2">
              {!hideBackButton ? (
                <button
                  onClick={onBack}
                  className="flex-shrink-0 flex items-center justify-center h-9 w-9 rounded-xl bg-brand-dark/10 hover:bg-brand-dark/20 text-brand-dark transition-colors mt-0.5"
                  title="Back to list"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </button>
              ) : (
                <div className="flex-shrink-0 h-9 w-9 rounded-xl bg-brand-dark/10 text-brand-dark flex items-center justify-center mt-0.5">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9.5 12 3l9 6.5V21a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1z"/></svg>
                </div>
              )}
              <div className="min-w-0 flex-1">
                
                  <>
                    <div className="flex items-center gap-2">
                      <h2
                        className={`text-sm font-semibold text-ui-text flex-1 min-w-0 leading-snug cursor-pointer ${addressExpanded ? '' : 'truncate'}`}
                        title={property.address.display_line || [property.address.street_number, property.address.street_name, property.address.street_suffix, `${property.address.city}, ${property.address.state} ${property.address.postal_code}`].filter(Boolean).join(', ')}
                        onClick={() => setAddressExpanded(v => !v)}
                      >
                        <span>{[property.address.street_number, property.address.pre_direction, property.address.street_name, property.address.street_suffix, property.address.post_direction].filter(Boolean).join(' ')}{property.address.unit_number ? `, ${property.address.unit_number}` : ''}</span>
                        <span className="text-xs font-normal text-ui-text2">{`, ${property.address.city}, ${property.address.state} ${property.address.postal_code}`}</span>
                      </h2>
                      {engagements.length > 0 && (
                        <div className="flex-shrink-0">
                          <StartEngagementInline propertyId={propertyId} addressLabel={property.address.display_line || undefined} label="+" onCreated={(id) => { pendingNewEngagementRef.current = id; }} />
                        </div>
                      )}
                    </div>
                  </>
                
                {property.property_type && (
                  <p className="text-[11px] text-ui-text3 mt-0.5">{humanizeLabel(property.property_type)}</p>
                )}
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto no-scrollbar">
            {/* Contact row — not sticky; scrolls away first as user scrolls up */}
            {headerPoc && (
              <div className="px-1 pt-2 pb-1">
                <ContactRow
                  contact={headerPoc}
                  compact
                  extraPhoneCount={extraContactCount}
                  onExtraClick={extraContactCount > 0 ? () => setTab('media') : undefined}
                />
              </div>
            )}

            {/* Engagement selector + Details — sticky: pins at top once contact scrolls away */}
            <div className="sticky top-0 z-10 bg-ui-card border-b border-ui-border px-1 py-1.5">
              <div className="flex items-center gap-2">
                {engagements.length > 0 ? (
                  <div className="relative flex-1 min-w-0" ref={engagementDropdownRef}>
                    <button
                      type="button"
                      onClick={() => {
                        if (tab !== 'engagement') {
                          setTab('engagement');
                        } else if (engagements.length > 1) {
                          setEngagementDropdownOpen(v => !v);
                        }
                      }}
                      className={`w-full flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-semibold transition-colors ${
                        tab === 'engagement'
                          ? 'bg-brand-dark/10 text-brand-dark border-brand-dark/40'
                          : 'bg-ui-subtle text-ui-text2 border-ui-border hover:bg-ui-hover'
                      }`}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                      <span className="flex-1 min-w-0 truncate text-left">
                        {openEngagementId
                          ? (engagements.find(e => e.engagement_id === openEngagementId)?.title ?? 'Engagement')
                          : `Engagement (${engagements.length})`}
                      </span>
                      {engagements.length > 1 && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={`flex-shrink-0 transition-transform ${engagementDropdownOpen ? 'rotate-180' : ''}`}><polyline points="6 9 12 15 18 9"/></svg>
                      )}
                    </button>
                    {engagementDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 z-20 bg-ui-card rounded-xl border border-ui-border shadow-lg overflow-hidden">
                        {engagements.map(e => (
                          <button
                            key={e.engagement_id}
                            type="button"
                            onClick={() => {
                              setOpenEngagementId(e.engagement_id);
                              setTab('engagement');
                              setEngagementDropdownOpen(false);
                              userBackedRef.current = false;
                            }}
                            className={`w-full flex items-center gap-2 px-3 py-2.5 text-xs text-left transition-colors hover:bg-ui-hover ${
                              e.engagement_id === openEngagementId ? 'text-brand-dark font-semibold bg-brand-dark/5' : 'text-ui-text'
                            }`}
                          >
                            <span className="flex-1 min-w-0 truncate">{e.title}</span>
                            <span className="flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-brand-dark/10 text-brand-dark uppercase tracking-wide hidden">{humanizeLabel(e.engagement_type)}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex-1" />
                )}
                <button
                  type="button"
                  onClick={() => setTab('media')}
                  className={`flex-shrink-0 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-semibold transition-colors ${
                    tab === 'media'
                      ? 'bg-brand-dark/10 text-brand-dark border-brand-dark/40'
                      : 'bg-ui-subtle text-ui-text2 border-ui-border hover:bg-ui-hover'
                  }`}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                  Details
                </button>
              </div>
            </div>

            {/* Main content — scrolls normally under the sticky engagement row */}
            <div className="px-1 flex flex-col gap-3 pt-2 pb-3">
              {tab === 'media' ? (
                <PropertyMediaTab
                  property={property}
                  propertyId={propertyId}
                  media={media}
                  measurements={measurements}
                  propNotes={propNotes}
                  contacts={propertyContacts}
                  engagementHolder={engagements.find(e => e.engagement_id === openEngagementId)?.holder_name ?? null}
                />
              ) : (
                <PropertyEngagementTab
                  propertyId={propertyId}
                  engagements={engagements}
                  openEngagementId={openEngagementId}
                  onOpenEngagement={(id) => { if (id === null) userBackedRef.current = true; setOpenEngagementId(id); }}
                  onSelectTask={onSelectTask}
                  engagementRev={engagementRev}
                  onEngagementStateChange={onEngagementStateChange}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Bridges the Engagements list ("open the first one") into WorkspacePropertyDetail — an
 * Engagement is always viewed through its property (see root CLAUDE.md's Property/Transaction
 * model), so opening one from a flat engagements list should never show a raw field dump; it
 * should land on the same property card + Engagement tab as opening from the Properties list. */
function EngagementAsPropertyDetail({ engagementId, records, onBack, onSelectTask, onEngagementStateChange }: {
  engagementId: string;
  records: Rec[];
  onBack: () => void;
  onSelectTask?: (id: string) => void;
  onEngagementStateChange?: (state: Rec | null) => void;
}) {
  const record = records.find(r => sv(r.engagement_id) === engagementId);
  const propertyId = record ? sv(record.property_id) : '';
  if (!propertyId) {
    return (
      <div className="flex flex-col h-full min-h-0">
        <button onClick={onBack} className="flex-shrink-0 flex items-center gap-1.5 px-1 py-2 text-xs text-brand-bright hover:opacity-80 transition-opacity">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          Back to list
        </button>
        <p className="text-center text-[#CF4148] py-8 text-sm">Could not find this engagement's property.</p>
      </div>
    );
  }
  return (
    <WorkspacePropertyDetail
      propertyId={propertyId}
      initialEngagementId={engagementId}
      onBack={onBack}
      onSelectTask={onSelectTask}
      onEngagementStateChange={onEngagementStateChange}
    />
  );
}


/** Media tab body — Photos, Measurements, Notes, minimal timeline (property created +
 *  engagement milestones only). All uploads attach directly to the property; no engagement
 *  gating (backend now accepts engagement_id=null on media/measurements). */
function PropertyMediaTab({ property, propertyId, media, measurements, propNotes, contacts, engagementHolder }: {
  property: NonNullable<ReturnType<typeof useGetPropertyDetailQuery>['data']>;
  propertyId: string;
  media: Array<{ media_id: string; media_type: string; s3_url: string; filename?: string | null; classifications: string[]; caption?: string | null }>;
  measurements: Array<{
    measurement_id: string; how_obtained: string; provider?: string | null; measured_on?: string | null;
    is_current_reference: boolean;
    values: { squares?: number | null; pitch?: string | null; ridge_length_ft?: number | null; total_area_sqft?: number | null };
    report_url?: string | null; report_filename?: string | null; notes?: string | null;
  }>;
  propNotes: Array<{ note_id: string; title?: string | null; note_type: string; source?: string | null; audio_url?: string | null; final_transcript?: string | null; clean_transcript?: string | null; review_summary?: string | null; created_on?: string | null }>;
  contacts: import('@/redux/internal-services/workspaceApiExtensions').PropertyContactLink[];
  engagementHolder?: string | null;
}) {
  const activeContacts = contacts.filter(c => c.is_current && c.contact);
  return (
    <>
      {engagementHolder && (
        <div className="flex items-center justify-between rounded-xl border border-ui-border bg-ui-subtle px-3 py-2">
          <span className="text-xs text-ui-text3">Engagement Holder</span>
          <span className="text-xs font-medium text-ui-text">{engagementHolder}</span>
        </div>
      )}
      {property.special_instructions && (
        <div className="rounded-lg border border-brand-bright/40 bg-brand-bright/5 px-3 py-2 text-xs text-ui-text2 flex items-start gap-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0 mt-0.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          {property.special_instructions}
        </div>
      )}

      {/* Contacts section — full list of all linked contacts for this property. */}
      {activeContacts.length > 0 && (
        <CardSection title="Contacts" count={activeContacts.length}>
          <div className="flex flex-col gap-2">
            {activeContacts.map(c => (
              <ContactRow
                key={c.contact_id}
                contact={{
                  name: c.contact!.display_name,
                  role: c.relationship_type || '',
                  is_primary: false,
                  phone: c.contact!.phones?.[0]?.value ?? null,
                  email: c.contact!.emails?.[0]?.value ?? null,
                }}
                extraPhoneCount={Math.max(0, (c.contact!.phones?.length ?? 1) - 1)}
              />
            ))}
          </div>
        </CardSection>
      )}

      <PropertyPhotosSection propertyId={propertyId} media={media} readOnly />
      <PropertyMeasurementsSection
        propertyId={propertyId}
        currentMeasurement={property.current_measurement}
        measurements={measurements}
        readOnly
      />
      <PropertyNotesSection propertyId={propertyId} notes={propNotes} readOnly />
      <PropertyFilesSection propertyId={propertyId} media={media} readOnly />

      {/* Minimal timeline — the property's created date + engagement milestones only. */}
      <div className="flex flex-col gap-2 px-1 pt-1">
        <h3 className="text-sm font-semibold text-ui-text">Timeline</h3>
        <TimelineList
          entries={buildMediaTabTimeline(property)}
        />
      </div>
    </>
  );
}

/** Rendered inside the Engagement tab when a specific engagement is opened. Wraps the
 *  common WorkflowCard with engagement-scoped upload surfaces (Photos, Files, Measurements)
 *  — these only render while that engagement is open, and everything uploaded from here
 *  carries the engagement_id. */
function EngagementDetailPanel({ propertyId, engagementId, onSelectTask, rev, onStateChange }: {
  propertyId: string;
  engagementId: string;
  onSelectTask?: (id: string) => void;
  rev?: number;
  onStateChange?: (state: Rec | null) => void;
}) {
  const { data: property } = useGetPropertyDetailQuery(propertyId);
  const { data: allMedia = [] } = useGetPropertyMediaQuery(propertyId);
  const { data: measurements = [] } = useGetPropertyMeasurementsQuery(propertyId);
  const { data: allNotes = [] } = useGetPropertyNotesQuery(propertyId);
  // Dedupes against WorkflowCard's identical call below — no extra network round-trip.
  const { data: workflowState } = useGetEngagementWorkflowStateQuery(engagementId, { skip: !engagementId });
  // Workflow transitions on this engagement create context_type='engagement' tasks — fetch
  // them here so anything the workflow spawned surfaces on the engagement it belongs to.
  // current_workflow_status_code scopes the checklist to what's still relevant: a still-OPEN
  // task always shows regardless of which status created it (some gates check a task by source
  // tag several transitions later — see TaskDAO.list), only a COMPLETED/CANCELLED task tied to
  // an already-passed status gets hidden, so the checklist stops re-showing done history once
  // the engagement moves on without risking hiding something a later gate still needs.
  const { data: taskList } = useGetTasksListQuery({
    context_type: 'engagement', context_entity_id: engagementId,
    current_workflow_status_code: workflowState?.current_status_code, limit: 50,
    mine: true, my_queues: true,
  });
  const engTasks = taskList?.data ?? [];
  // Filter to just this engagement's artifacts — the same property-scoped lists are shared
  // across the property, so scope by engagement_id before handing to the sections.
  const engMedia = allMedia.filter(m => m.engagement_id === engagementId);
  const engMeasurements = measurements.filter(m => m.source_engagement_id === engagementId);
  const engNotes = allNotes.filter(n => n.engagement_id === engagementId);
  const currentMeas = property?.current_measurement && engMeasurements.some(m => m.measurement_id === property.current_measurement?.measurement_id)
    ? property.current_measurement
    : undefined;

  // Which sections are actually required at this point in the workflow — driven by governance
  // gates on the engagement's currently-available transitions (RTK Query dedupes against
  // WorkflowCard's identical calls, no extra network round-trip). No gate for a section on any
  // available transition = that section isn't relevant right now, so it's hidden rather than
  // shown empty. Notes has no corresponding gate concept yet, so it always shows.
  const { data: availableTransitions = [] } = useListAvailableTransitionsQuery(engagementId, { skip: !engagementId });
  const { data: customization } = useGetWorkflowCustomizationQuery(undefined, { skip: !engagementId });
  const availableTransitionCodes = new Set(availableTransitions.map(t => t.transition_code));
  const activeGateTypes = new Set(
    (customization?.governance_gates ?? [])
      .filter(g => availableTransitionCodes.has(g.target_ref))
      .map(g => g.condition_type),
  );
  const showPhotos = activeGateTypes.has('required_photos');
  const showMeasurements = activeGateTypes.has('required_measurement');
  const showFiles = activeGateTypes.has('required_document');

  return (
    <div className="flex flex-col gap-3">
        <WorkflowCard
          engagementId={engagementId}
          rev={rev}
          onStateChange={onStateChange}
          tasks={engTasks}
          onSelectTask={onSelectTask}
        />
      {showPhotos && <PropertyPhotosSection propertyId={propertyId} engagementId={engagementId} media={engMedia} />}
      {showMeasurements && (
        <PropertyMeasurementsSection
          propertyId={propertyId}
          engagementId={engagementId}
          currentMeasurement={currentMeas}
          measurements={engMeasurements}
        />
      )}
      <PropertyNotesSection propertyId={propertyId} engagementId={engagementId} notes={engNotes} />
      {showFiles && <PropertyFilesSection propertyId={propertyId} engagementId={engagementId} media={engMedia} />}
      <EngagementActivitySection engagementId={engagementId} />
    </div>
  );
}


interface MinimalTimelineEntry { date: string; label: string; description?: string | null; latest?: boolean }

function buildMediaTabTimeline(property: NonNullable<ReturnType<typeof useGetPropertyDetailQuery>['data']>): MinimalTimelineEntry[] {
  // Only the property creation + one entry per engagement creation — no other timeline events.
  // The PropertyDetail payload doesn't carry the property's own created_on, so the earliest
  // engagement's created_on doubles as the "Property created" marker.
  const sorted = [...property.engagements]
    .filter(e => !!e.created_on)
    .sort((a, b) => (a.created_on || '').localeCompare(b.created_on || ''));
  if (sorted.length === 0) return [];

  const entries: MinimalTimelineEntry[] = [
    { date: sorted[0].created_on as string, label: 'Property created' },
    ...sorted.map(e => ({
      date: e.created_on as string,
      label: `Engagement created: ${e.title}`,
      description: humanizeLabel(e.engagement_type),
    })),
  ];
  entries[entries.length - 1].latest = true;
  return entries;
}

function TimelineList({ entries }: { entries: MinimalTimelineEntry[] }) {
  if (entries.length === 0) return <p className="text-xs text-ui-text3 py-2">No history yet.</p>;
  return (
    <div className="flex flex-col">
      {entries.map((entry, i) => (
        <div key={i} className="flex gap-3">
          <div className="flex flex-col items-center">
            <span className="text-[11px] text-ui-text3 whitespace-nowrap w-16 text-right pt-0.5">{fmtDate(entry.date)}</span>
          </div>
          <div className="flex flex-col items-center">
            <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${entry.latest ? 'bg-brand-bright' : 'bg-ui-border'}`} />
            {i < entries.length - 1 && <span className="w-px flex-1 bg-ui-border" />}
          </div>
          <div className="pb-4 min-w-0">
            <p className="text-sm font-medium text-ui-text">{entry.label}</p>
            {entry.description && <p className="text-xs text-ui-text2 mt-0.5">{entry.description}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Engagement tab body — 0 engagements = "Start Engagement" empty state; 1 auto-opens into
 *  its WorkflowCard; 2+ shows minimal cards (name/type/status/holder) that reveal the
 *  WorkflowCard on tap. The embedded WorkflowCard owns its own upload / notes / files
 *  scoped to that engagement — only visible while that engagement is open. */
function PropertyEngagementTab({ propertyId, engagements, openEngagementId, onOpenEngagement, onSelectTask, engagementRev, onEngagementStateChange }: {
  propertyId: string;
  engagements: Array<{ engagement_id: string; title: string; engagement_type: string; stage: string; current_status: string; holder_id?: string | null; holder_name?: string | null }>;
  openEngagementId: string | null;
  onOpenEngagement: (id: string | null) => void;
  onSelectTask?: (id: string) => void;
  engagementRev?: number;
  onEngagementStateChange?: (state: Rec | null) => void;
}) {
  if (openEngagementId) {
    return (
      <div className="flex-1 min-h-0 flex flex-col">
        <EngagementDetailPanel
          propertyId={propertyId}
          engagementId={openEngagementId}
          onSelectTask={onSelectTask}
          rev={engagementRev}
          onStateChange={onEngagementStateChange}
        />
      </div>
    );
  }

  if (engagements.length === 0) {
    return <StartEngagementEmpty propertyId={propertyId} />;
  }

  return (
    <div className="flex flex-col gap-2 px-1">
      {engagements.map(e => (
        <button
          key={e.engagement_id}
          type="button"
          onClick={() => onOpenEngagement(e.engagement_id)}
          className="flex flex-col gap-1.5 rounded-xl border border-ui-border bg-ui-subtle px-3 py-2.5 text-left hover:bg-ui-hover transition-colors"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-ui-text truncate">{e.title}</span>
            <span className="flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide bg-brand-dark/10 text-brand-dark">
              {humanizeLabel(e.engagement_type)}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap text-[11px] text-ui-text2">
            <span className="font-medium px-2 py-0.5 rounded-full bg-ui-card border border-ui-border">
              {e.current_status || '—'}
            </span>
            {e.holder_name && (
              <span className="flex items-center gap-1">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                {e.holder_name}
              </span>
            )}
          </div>
        </button>
      ))}
    </div>
  );
}

function StartEngagementEmpty({ propertyId, addressLabel }: { propertyId: string; addressLabel?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 gap-3 text-center px-6">
      <div className="h-12 w-12 rounded-2xl bg-brand-dark/10 text-brand-dark flex items-center justify-center">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
      </div>
      <p className="text-sm font-medium text-ui-text">No engagements yet</p>
      <p className="text-xs text-ui-text3">Start one to track sales, jobs, warranty events for this property.</p>
      <StartEngagementInline propertyId={propertyId} addressLabel={addressLabel} label="Start Engagement" prominent />
    </div>
  );
}

function StartEngagementInline({ propertyId, addressLabel, label = '+ Start another engagement', prominent = false, onCreated }: {
  propertyId: string;
  /** The property's one-line address. Appended to the "⋯" entry's title so it matches the label
   *  the backend builds for the same view (engagement titles are serials — "Replacement 2" alone
   *  doesn't say which property). */
  addressLabel?: string;
  label?: string;
  prominent?: boolean;
  onCreated?: (engagementId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [engagementType, setEngagementType] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [createEngagement, { isLoading }] = useCreateEngagementMutation();
  const dispatch = useDispatch();
  const onUserView = useContext(UserViewContext);

  const { data: catalogueData } = useListCatalogueEntriesQuery({ prefix: 'workflow_engine' });
  const engagementTypeOptions = useMemo<Array<{ id: string; label: string }>>(() => {
    const entry = catalogueData?.data.find((c) => c.key === 'workflow_engine');
    return (entry?.effective_value?.engagement_types as Array<{ id: string; label: string }> | undefined) ?? [];
  }, [catalogueData]);

  useEffect(() => {
    if (!engagementType && engagementTypeOptions.length) setEngagementType(engagementTypeOptions[0].id);
  }, [engagementTypeOptions, engagementType]);

  async function submit() {
    if (!engagementType) { setError('Choose an engagement type.'); return; }
    setError(null);
    try {
      const result = await createEngagement({ propertyId, engagementType }).unwrap();
      dispatch(workspaceApi.util.invalidateTags([{ type: 'Property', id: propertyId }]));
      setOpen(false);
      if (result?.engagement_id) onCreated?.(result.engagement_id);
      // Add the just-opened engagement to the "⋯" menu. Deliberately the SAME shape SAM's
      // create_engagement / get_engagement publish (module 'properties', record_id = property,
      // filter.initial_engagement_id) so creating one here and asking SAM for it collapse to a
      // single entry instead of two near-identical rows.
      if (result?.engagement_id) {
        onUserView?.({
          module: 'properties',
          params: { initial_engagement_id: result.engagement_id },
          recordId: propertyId,
          title: addressLabel ? `${result.title} · ${addressLabel}` : result.title,
          subtitle: result.display_labels?.status,
        });
      }
    } catch (e: unknown) {
      const msg = (e as { data?: { message?: string } })?.data?.message || 'Could not start engagement.';
      setError(msg);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={prominent
          ? 'text-sm font-semibold px-4 py-2 rounded-full bg-brand-dark text-white hover:opacity-90'
          : 'text-xs font-medium px-3 py-1.5 rounded-full bg-brand-dark/10 text-brand-dark hover:bg-brand-dark/15'
        }
      >
        {label}
      </button>
    );
  }

  return (
    <div className="rounded-lg border border-ui-border bg-ui-card p-3 flex flex-col gap-2 w-full">
      <label className="flex flex-col gap-1">
        <span className="text-[10px] text-ui-text3 uppercase tracking-wide">Type</span>
        <select
          value={engagementType}
          onChange={e => setEngagementType(e.target.value)}
          disabled={!engagementTypeOptions.length}
          autoFocus
          className="rounded-lg border border-ui-border bg-ui-subtle px-3 py-2 text-sm focus:outline-none focus:border-brand-dark disabled:opacity-60"
        >
          {engagementTypeOptions.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      </label>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={() => { setOpen(false); setError(null); }} className="text-xs px-3 py-1 rounded-full text-ui-text2 hover:bg-ui-hover">Cancel</button>
        <button type="button" disabled={!engagementType || isLoading} onClick={submit} className="text-xs font-medium px-3 py-1 rounded-full bg-brand-dark text-white disabled:opacity-40 hover:opacity-90">
          {isLoading ? 'Starting…' : 'Start engagement'}
        </button>
      </div>
    </div>
  );
}

// ─── WorkspaceNotificationsPanel ─────────────────────────────────────────────
// Compact notification list for the workspace sidebar.
// Approvals are handled inline — clicking a pending-approval notification opens
// ApprovalDetailPanel here rather than navigating to a standalone page.

function WorkspaceNotificationsPanel({
  externalApprovalId,
  onExternalApprovalClose,
  onApprovalSelected,
  onEntityLink,
}: {
  /** Approval to open directly (e.g. from /?approval=<id> URL param routed through ChatPage). */
  externalApprovalId?: string | null;
  onExternalApprovalClose?: () => void;
  /** Emits context when the user opens an approval detail so the agent knows which approval is on screen. */
  onApprovalSelected?: (approvalId: string | null) => void;
  /** Called when the user clicks an entity link on a notification (e.g. linked property / task). */
  onEntityLink?: (module: string, entityId: string, entityType?: string) => void;
}) {
  const [tab, setTab] = useState<'all' | 'unread' | 'action'>('all');
  const [selectedApprovalId, setSelectedApprovalId] = useState<string | null>(externalApprovalId ?? null);
  const NOTIFICATIONS_PAGE_SIZE = 20;
  const [skip, setSkip] = useState(0);

  // Reset pagination whenever the active tab changes
  useEffect(() => {
    setSkip(0);
  }, [tab]);

  // Sync when the external approval changes (e.g. new ?approval= param while panel is already open)
  useEffect(() => {
    if (externalApprovalId) setSelectedApprovalId(externalApprovalId);
  }, [externalApprovalId]);

  const { data: approvalDetail, isLoading: approvalLoading } = useGetApprovalByIdQuery(
    selectedApprovalId!,
    { skip: !selectedApprovalId },
  );

  // An approval notification should land the user on the engagement workspace directly —
  // no intermediate approval-detail screen. Redirect as soon as the approval resolves.
  useEffect(() => {
    if (approvalDetail?.engagement_id && onEntityLink) {
      onEntityLink('WFL', approvalDetail.engagement_id, 'engagement');
      setSelectedApprovalId(null);
      onExternalApprovalClose?.();
      onApprovalSelected?.(null);
    }
  }, [approvalDetail, onEntityLink, onExternalApprovalClose, onApprovalSelected]);

  const queryParams = {
    ...(tab === 'unread' ? { read: false } : {}),
    ...(tab === 'action' ? { action_required: true } : {}),
    archived: false as boolean,
    limit: NOTIFICATIONS_PAGE_SIZE,
    skip,
  };
  const { data, isFetching } = useGetInboxQuery(queryParams);
  const { data: counts } = useGetInboxCountsQuery();

  // Accumulate pages locally since RTK Query treats each skip value as a separate cache entry.
  const [accumulatedItems, setAccumulatedItems] = useState<InAppNotificationOut[]>([]);
  useEffect(() => {
    if (!data) return;
    setAccumulatedItems((prev) => (skip === 0 ? data.items : [...prev, ...data.items]));
  }, [data, skip]);

  const items = accumulatedItems;
  const total = data?.total ?? 0;
  const hasMore = items.length < total;

  const tabs: Array<{ id: 'all' | 'unread' | 'action'; label: string }> = [
    { id: 'all', label: 'All' },
    { id: 'unread', label: counts?.unread ? `Unread (${counts.unread})` : 'Unread' },
    { id: 'action', label: counts?.action_required ? `Action (${counts.action_required})` : 'Action' },
  ];

  // ── Inline approval detail view ───────────────────────────────────────────
  if (selectedApprovalId) {
    return (
      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={() => { setSelectedApprovalId(null); onExternalApprovalClose?.(); onApprovalSelected?.(null); }}
          className="flex items-center gap-1.5 text-xs font-medium text-brand-dark hover:underline self-start"
        >
          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to notifications
        </button>
        {approvalLoading ? (
          <div className="flex justify-center py-8"><Spinner size="sm" /></div>
        ) : approvalDetail ? (
          <ApprovalDetailPanel
            approval={approvalDetail}
            onOpenEngagement={onEntityLink ? (id) => onEntityLink('WFL', id, 'engagement') : undefined}
          />
        ) : (
          <p className="text-sm text-ui-text2 text-center py-8">Approval not found or no longer available.</p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-1 flex-wrap">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
              tab === t.id ? 'bg-brand-dark text-white' : 'text-ui-text2 hover:bg-ui-hover'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {isFetching && items.length === 0 ? (
        <div className="flex justify-center py-8"><Spinner size="sm" /></div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center gap-1">
          <p className="text-sm font-medium text-ui-text2">All caught up</p>
          <p className="text-xs text-ui-text3">No notifications to show.</p>
        </div>
      ) : (
        <div className="divide-y divide-ui-border rounded-xl border border-ui-border bg-ui-card overflow-hidden">
          {items.map((n) => {
            const approvalMatch = n.action_route?.match(/^\/workflow\/approvals\/(.+)$/);
            const approvalId = approvalMatch ? approvalMatch[1] : null;
            const hasPendingApproval = n.is_action_required && n.action_status === 'Pending' && !!approvalId;
            const hasEntityLink = !!onEntityLink && !!n.source_entity_id && !!n.source_module && !hasPendingApproval;
            const entityAction = hasEntityLink
              ? () => onEntityLink!(n.source_module!, n.source_entity_id!, n.source_entity_type ?? undefined)
              : undefined;
            return (
              <div key={n.id}>
                <NotificationItem
                  notification={n}
                  compact
                  disableNavigation
                  onAction={hasPendingApproval ? () => { setSelectedApprovalId(approvalId!); onApprovalSelected?.(approvalId!); } : entityAction}
                />
              </div>
            );
          })}
        </div>
      )}
      {items.length > 0 && hasMore && (
        <button
          type="button"
          onClick={() => setSkip((prev) => prev + NOTIFICATIONS_PAGE_SIZE)}
          disabled={isFetching}
          className="self-center px-3 py-1.5 rounded-md text-xs font-medium text-brand-dark hover:bg-ui-hover disabled:opacity-50"
        >
          {isFetching ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  );
}

// ─── UXView ───────────────────────────────────────────────────────────────────

export const UXView = ({
  cards, messageStrip, onSave, onFacet, openCards, setOpenCards, hidePropertyBack, onUserOpenProperty, onExpandStrip,
  isWaiting,
  history = [], onReplay, historyHasMore, onHistoryLoadMore, taskTab: _taskTab, setTaskTab: _setTaskTab, meetingTab: _meetingTab, onActiveMeeting: _onActiveMeeting, onActiveTask: _onActiveTask, onNoteSynced, onStartNoteTaker,
  onNotesListChanged, openApprovalId, onCloseApproval, onUserOpenCard, onEngagementStateChange, onApprovalSelected,
  openNotificationsPanel, onCloseNotificationsPanel, onOpenActionView, onUserView,
  directEngagementId: directEngagementIdProp = null, onDirectEngagementChange,
}: {
  cards: CardsMessage | null;
  messageStrip: string;
  /** SAM's next reply for THIS turn hasn't arrived yet — show the same bouncing-dot indicator
   *  the chat thread shows, in place of the (now-stale) previous messageStrip text. */
  isWaiting?: boolean;
  onSave: OnSave;
  onFacet: OnFacet;
  openCards: Partial<Record<SamModule, string | null>>;
  setOpenCards: React.Dispatch<React.SetStateAction<Partial<Record<SamModule, string | null>>>>;
  /** When true, WorkspacePropertyDetail hides its back button (SAM auto-opened, no list to return to). */
  hidePropertyBack?: boolean;
  /** Reset hidePropertyBack when the user manually opens a property from the list. */
  onUserOpenProperty?: () => void;
  onExpandStrip?: () => void;
  history?: RecentEntry[];
  onReplay?: (e: RecentEntry) => void;
  historyHasMore?: boolean;
  onHistoryLoadMore?: () => void;
  /** A view the USER opened without an agent turn — a manual filter/tab change on a list card, or
   *  a record created from a card's `+`. Adds it to the same "⋯" menu the agent's views land in
   *  (`useRecentViews.record` with a non-'agent' source, which also persists it). */
  onUserView?: (view: UserViewReport) => void;
  taskTab?: TaskDetailTab;
  setTaskTab?: (tab: TaskDetailTab) => void;
  meetingTab?: 'details' | 'notes' | 'activity';
  /** Push the open task detail's current fields to SAM as active_task; null clears it. */
  onActiveTask?: (task: Rec | null) => void;
  /** Push the open meeting detail's current fields to SAM as active_meeting; null clears it. */
  onActiveMeeting?: (meeting: Rec | null) => void;
  /** Push the open note detail's current fields to SAM as active_note; null clears it. */
  onNoteSynced?: (note: Rec | null) => void;
  /** Open the Note Taker pre-linked to a meeting. */
  onStartNoteTaker?: (contextType: string, entityId: string) => void;
  /** A note was deleted/saved/converted from the note detail view — refresh the on-screen notes
   *  list card so a stale record doesn't linger when the user navigates back to it. */
  onNotesListChanged?: () => void;
  /** Approval ID to open inline in the workspace (from /?approval=<id> URL param).
   *  Forces the workspace into the notifications panel with the approval detail shown. */
  openApprovalId?: string | null;
  /** Called when the inline approval panel is dismissed so the caller can clear the state. */
  onCloseApproval?: () => void;
  /** Emit opened-card context when the user manually opens a card (not SAM-initiated). The agent
   *  receives a [SCREEN UPDATE] noting which item is now on screen. Pass null to clear (back). */
  onUserOpenCard?: (module: SamModule, id: string | null) => void;
  /** Switch workspace to a module (fetch rows, update panel) — used by notification entity links
   *  to actually navigate to the tasks/meetings panel after closing the notifications overlay. */
  onOpenActionView?: (module: SamModule, filter: Record<string, unknown>) => Promise<void>;
  /** Push the embedded engagement's live workflow state to SAM (active_engagement_workflow context)
   *  from within WorkspacePropertyDetail — same as emitWorkflowContext in useChatWorkspace. */
  onEngagementStateChange?: (state: Rec | null) => void;
  /** Called when the user selects a notification's approval detail inside WorkspaceNotificationsPanel
   *  so the agent learns which approval is being reviewed. Null clears (back to list). */
  onApprovalSelected?: (approvalId: string | null) => void;
  /** Set by ChatPage when the user clicks the bell icon — opens the notifications workspace panel. */
  openNotificationsPanel?: boolean;
  /** Called by UXView when the notifications panel should be closed (entity link clicked, etc.). */
  onCloseNotificationsPanel?: () => void;
  /** Lifted from UXView to ChatPage so mobile workspace stays visible during engagement navigation. */
  directEngagementId?: string | null;
  /** Setter for directEngagementId — called when user navigates to/from an engagement detail. */
  onDirectEngagementChange?: (engagementId: string | null) => void;
}) => {
  // Track strip changes to replay the highlight animation on each new SAM response.
  const prevStripRef = useRef('');
  const prevWaitingRef = useRef(false);
  const [stripKey, setStripKey] = useState(0);
  const [isWorkspaceRefreshing, setIsWorkspaceRefreshing] = useState(false);
  // Set when the user clicks a specific engagement sub-card in the Properties list (a plain
  // local click, not a SAM action) — lands WorkspacePropertyDetail directly on that engagement
  // instead of its Engagement tab's multi-engagement picker. cards.filter?.initial_engagement_id
  // (set by SAM's get_engagement) still takes priority when both are present.
  // Scoped to its source property so that opening a different property via SAM never inherits
  // a stale engagement ID from a previous sub-card click on a different property.
  const [pendingEngagement, setPendingEngagement] = useState<{ propertyId: string; engagementId: string } | null>(null);
  // directEngagementId is lifted to ChatPage so mobile workspace stays visible during navigation.
  const directEngagementId = directEngagementIdProp;
  const setDirectEngagementId = onDirectEngagementChange ?? (() => {});
  useEffect(() => {
    if (messageStrip && messageStrip !== prevStripRef.current) {
      prevStripRef.current = messageStrip;
      setStripKey(k => k + 1);
    } else if (isWaiting && !prevWaitingRef.current) {
      // The strip can also first appear with no messageStrip yet (dots-only, isWaiting just
      // turned true) — that's a new event too, so replay the glow here as well.
      setStripKey(k => k + 1);
    }
    prevWaitingRef.current = !!isWaiting;
  }, [messageStrip, isWaiting]);

  return (
  <UserViewContext.Provider value={onUserView}>
  <div className="flex flex-col h-full min-h-0">
    {/* Agent reply strip — glowing border highlight on each new SAM response. Shows even with no
        messageStrip yet when isWaiting, so the dots below appear as soon as the turn starts. */}
    {(messageStrip || isWaiting) && (
      <div
        key={stripKey}
        onClick={isWaiting ? undefined : onExpandStrip}
        role={!isWaiting && onExpandStrip ? 'button' : undefined}
        className={`flex-shrink-0 w-full text-left px-2 py-1 border-b border-ui-border bg-ui-subtle ${stripKey > 0 ? 'sam-reply-new' : ''}`}
      >
        <div
          className={`rounded-lg border border-ui-border border-l-[3px] border-l-brand-dark/40 bg-ui-card px-2.5 py-1.5 ${!isWaiting && onExpandStrip ? 'cursor-pointer hover:bg-ui-hover' : ''}`}
        >
          {isWaiting ? (
            <TypingDots />
          ) : (
            <>
              <div className="text-sm text-ui-text leading-snug overflow-hidden" style={{ maxHeight: '5rem' }}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
                    ul: ({ children }) => <ul className="list-disc pl-4 mb-1 space-y-0.5">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal pl-4 mb-1 space-y-0.5">{children}</ol>,
                    li: ({ children }) => <li className="leading-snug">{children}</li>,
                    strong: ({ children }) => <strong className="font-semibold text-ui-text">{children}</strong>,
                    em: ({ children }) => <em className="italic text-ui-text2">{children}</em>,
                    h1: ({ children }) => <p className="font-bold mb-1">{children}</p>,
                    h2: ({ children }) => <p className="font-bold mb-1">{children}</p>,
                    h3: ({ children }) => <p className="font-semibold mb-1">{children}</p>,
                    a: ({ href, children }) => <a href={href} className="underline text-indigo-500 hover:text-indigo-400" target="_blank" rel="noopener noreferrer">{children}</a>,
                    code: ({ children }) => <code className="bg-ui-subtle rounded px-1 text-xs font-mono">{children}</code>,
                  }}
                >
                  {messageStrip}
                </ReactMarkdown>
              </div>
              {onExpandStrip && <span className="mt-0.5 inline-block text-[11px] text-brand-bright">Tap to open chat</span>}
            </>
          )}
        </div>
      </div>
    )}
    {/* Workspace header — title + refresh button + history menu (⋯) for all modules */}
    <div className="flex-shrink-0 flex items-center justify-between px-3 py-1 border-b border-ui-border gap-2">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-ui-text2 truncate">
        {openApprovalId && !openNotificationsPanel && cards?.module !== 'notifications' ? 'Approval Review' : openNotificationsPanel || cards?.module === 'notifications' ? 'Notifications' : directEngagementId ? 'Engagement' : cards ? (cards.title || humanizeLabel(cards.module)) : 'Workspace'}
      </span>
      <div className="flex items-center gap-0.5">
        {cards && (
          <WorkspaceRefreshButton
            module={cards.module}
            openId={openCards[cards.module]}
            onRefreshChange={setIsWorkspaceRefreshing}
          />
        )}
        <HistoryMenu history={history} caption={cards?.caption} onReplay={onReplay} hasMore={historyHasMore} onLoadMore={onHistoryLoadMore} />
      </div>
    </div>
    {/* Refresh progress bar — thin shimmer strip visible while workspace is reloading */}
    <div className="flex-shrink-0 relative h-0.5 overflow-hidden bg-transparent">
      {isWorkspaceRefreshing && (
        <div className="workspace-refresh-bar bg-brand-bright opacity-75" />
      )}
    </div>
<div className={`flex-1 min-h-0 ${cards?.module === 'calendar' ? 'overflow-hidden flex flex-col' : 'overflow-y-auto no-scrollbar px-2 py-1'}`}>
      {/* Notifications panel — shown from: bell icon (openNotificationsPanel), approval URL param
          (openApprovalId), or SAM opening the notifications module (cards.module === 'notifications').
          All three cases render the same panel; it handles approvals inline via externalApprovalId. */}
      {openApprovalId || openNotificationsPanel || cards?.module === 'notifications' ? (
        <WorkspaceNotificationsPanel
          externalApprovalId={openApprovalId}
          onExternalApprovalClose={onCloseApproval}
          onApprovalSelected={onApprovalSelected}
          onEntityLink={(module, entityId, entityType) => {
            onCloseNotificationsPanel?.();
            const mod = module?.toUpperCase();
            if (mod === 'TSK' || mod === 'TASK') {
              setOpenCards(prev => ({ ...prev, tasks: entityId }));
              void onOpenActionView?.('tasks', {});
            } else if (mod === 'MTG' || mod === 'MEETING') {
              setOpenCards(prev => ({ ...prev, meetings: entityId }));
              void onOpenActionView?.('meetings', {});
            } else if (module === 'WFL' && entityType === 'engagement') {
              setDirectEngagementId(entityId);
            } else if (mod === 'COM') {
              onUserOpenCard?.('communications', entityId);
            } else if (mod === 'USR') {
              onUserOpenCard?.('users', entityId);
            } else {
              onUserOpenCard?.(module as SamModule, entityId);
            }
          }}
        />
      ) : directEngagementId ? (
        <WorkspaceDirectEngagementDetail
          engagementId={directEngagementId}
          onBack={() => setDirectEngagementId(null)}
          onSelectTask={(id) => { setDirectEngagementId(null); setOpenCards(prev => ({ ...prev, tasks: id })); onUserOpenCard?.('tasks', id); }}
          onEngagementStateChange={onEngagementStateChange}
        />
      ) : cards ? (
        cards.module === 'tasks' ? (
          <TaskList
            key={JSON.stringify(cards.filter ?? {})}
            initialStatus={cards.filter?.status as string | undefined}
            initialPriority={cards.filter?.priority as string | undefined}
            initialAssigneeUserId={cards.filter?.assignee_user_id as string | undefined}
            initialOwnerUserId={cards.filter?.owner_user_id as string | undefined}
            initialUpdatedByUserId={cards.filter?.updated_by_user_id as string | undefined}
            initialSearch={cards.filter?.search as string | undefined}
            initialOverdue={!!cards.filter?.overdue}
            initialMine={typeof cards.filter?.mine === 'boolean' ? cards.filter.mine : true}
            initialRecords={cards.records as unknown as TaskFull[]}
            initialExpandedId={openCards['tasks'] ?? undefined}
            singleTaskId={openCards['tasks'] ?? undefined}
            onExitSingleTask={() => setOpenCards(prev => ({ ...prev, tasks: null }))}
            initialSortBy={cards.filter?.sort_by as string | undefined}
            initialSortDir={cards.filter?.sort_dir as number | undefined}
            onFiltersChange={(params) => onUserView?.({ module: 'tasks', params })}
          />
        ) : cards.module === 'notes' ? (
          openCards['notes'] ? (
            <WorkspaceNoteDetail
              noteId={openCards['notes']}
              onBack={() => { setOpenCards(prev => ({ ...prev, notes: null })); }}
              onOpenTask={(id) => { setOpenCards(prev => ({ ...prev, tasks: id })); onUserOpenCard?.('tasks', id); }}
              onNoteSynced={onNoteSynced}
              onNotesListChanged={onNotesListChanged}
            />
          ) : (
            <InlineCards
              module={cards.module}
              records={cards.records as Rec[]}
              facets={cards.facets}
              onSave={onSave}
              onFacet={onFacet}
              openedId={openCards[cards.module]}
              onOpenChange={id => { setOpenCards(prev => ({ ...prev, [cards.module]: id })); onUserOpenCard?.(cards.module, id); }}
            />
          )
        ) : cards.module === 'meetings' ? (
          <WorkspaceMeetingList
            key={JSON.stringify(cards.filter ?? {})}
            initialStatus={cards.filter?.status as string | undefined}
            initialMeetingType={cards.filter?.meeting_type as string | undefined}
            initialSearch={cards.filter?.search as string | undefined}
            initialOrganizerUserId={cards.filter?.organizer_user_id as string | undefined}
            initialUpdatedByUserId={cards.filter?.updated_by_user_id as string | undefined}
            initialExpandedId={openCards['meetings'] ?? undefined}
            singleMeetingId={openCards['meetings'] ?? undefined}
            onExitSingleMeeting={() => setOpenCards(prev => ({ ...prev, meetings: null }))}
            initialSortBy={cards.filter?.sort_by as string | undefined}
            initialSortDir={cards.filter?.sort_dir as number | undefined}
            onStartNoteTaker={onStartNoteTaker}
            onFiltersChange={(params) => onUserView?.({ module: 'meetings', params })}
          />
        ) : cards.module === 'my_todos' ? (
          <MyTodosList
            records={cards.records as Rec[]}
            onSelectTask={(id) => { setOpenCards(prev => ({ ...prev, tasks: id })); onUserOpenCard?.('tasks', id); }}
            onSelectMeeting={(id) => { setOpenCards(prev => ({ ...prev, my_todos: id })); onUserOpenCard?.('my_todos', id); }}
          />
        ) : cards.module === 'calendar' ? (
          <CalendarPage
            onTaskClick={(id) => { setOpenCards(prev => ({ ...prev, tasks: id })); onUserOpenCard?.('tasks', id); }}
            onMeetingClick={(id) => { setOpenCards(prev => ({ ...prev, meetings: id })); onUserOpenCard?.('meetings', id); }}
          />
        ) : cards.module === 'properties' ? (
          openCards['properties'] ? (
            <WorkspacePropertyDetail
              key={openCards['properties']}
              propertyId={openCards['properties']}
              // Set by get_engagement (via record_id=property_id + filter.initial_engagement_id)
              // when the user named a SPECIFIC engagement — lands there directly instead of the
              // Engagement tab's own multi-engagement picker. Absent for a plain search_engagement panel view.
              initialEngagementId={
                (cards.filter?.initial_engagement_id as string | undefined)
                ?? (pendingEngagement && pendingEngagement.propertyId === openCards['properties'] ? pendingEngagement.engagementId : undefined)
                ?? undefined
              }
              hideBackButton={hidePropertyBack}
              onBack={() => {
                setOpenCards(prev => ({ ...prev, properties: null }));
                setPendingEngagement(null);
                onUserOpenProperty?.();
                onUserOpenCard?.('properties', null);
              }}
              onSelectTask={(id) => { setOpenCards(prev => ({ ...prev, tasks: id })); onUserOpenCard?.('tasks', id); }}
            />
          ) : (
            <WorkspacePropertyList
              key={JSON.stringify(cards.filter ?? {})}
              initialSearch={cards.filter?.search as string | undefined}
              initialEngagementStatus={cards.filter?.engagement_status as 'active' | 'all' | undefined}
              initialStatus={cards.filter?.status as string | undefined}
              initialCity={cards.filter?.city as string | undefined}
              initialState={cards.filter?.state as string | undefined}
              initialPostalCode={cards.filter?.postal_code as string | undefined}
              initialPropertyType={cards.filter?.property_type as 'residential' | 'commercial' | 'industrial' | undefined}
              initialPropertyStatus={cards.filter?.property_status as string | undefined}
              initialEngagementType={cards.filter?.engagement_type as string | undefined}
              initialSource={cards.filter?.source as string | undefined}
              initialCreatedAfter={cards.filter?.created_after as string | undefined}
              initialCreatedBefore={cards.filter?.created_before as string | undefined}
              initialUpdatedAfter={cards.filter?.updated_after as string | undefined}
              initialUpdatedBefore={cards.filter?.updated_before as string | undefined}
              initialAssignee={cards.filter?.assignee as string | undefined}
              initialAssigneeRole={cards.filter?.assignee_role as 'sales_agent' | 'project_manager' | 'assigned_user' | undefined}
              initialContactName={cards.filter?.contact_name as string | undefined}
              initialContactRole={cards.filter?.contact_role as string | undefined}
              onSelectProperty={(id) => {
                onUserOpenProperty?.();
                setOpenCards(prev => ({ ...prev, properties: id }));
                onUserOpenCard?.('properties', id);
              }}
              onSelectEngagement={(propertyId, engagementId) => {
                onUserOpenProperty?.();
                setPendingEngagement({ propertyId, engagementId });
                setOpenCards(prev => ({ ...prev, properties: propertyId }));
                onUserOpenCard?.('properties', propertyId);
              }}
              onFiltersChange={(params) => onUserView?.({ module: 'properties', params })}
            />
          )
        ) : cards.module === 'engagements' ? (
          openCards['engagements'] ? (
            <EngagementAsPropertyDetail
              engagementId={openCards['engagements']}
              records={cards.records as Rec[]}
              onBack={() => { setOpenCards(prev => ({ ...prev, engagements: null })); onUserOpenCard?.('engagements', null); }}
              onSelectTask={(id) => { setOpenCards(prev => ({ ...prev, tasks: id })); onUserOpenCard?.('tasks', id); }}
              onEngagementStateChange={onEngagementStateChange}
            />
          ) : (
            <InlineCards
              module={cards.module}
              records={cards.records as Rec[]}
              facets={cards.facets}
              onSave={onSave}
              onFacet={onFacet}
              openedId={null}
              onOpenChange={id => { setOpenCards(prev => ({ ...prev, engagements: id })); onUserOpenCard?.('engagements', id); }}
            />
          )
        ) : (
          <InlineCards
            module={cards.module}
            records={cards.records as Rec[]}
            facets={cards.facets}
            onSave={onSave}
            onFacet={onFacet}
            openedId={openCards[cards.module]}
            onOpenChange={id => { setOpenCards(prev => ({ ...prev, [cards.module]: id })); onUserOpenCard?.(cards.module, id); }}
          />
        )
      ) : (
        <div className="flex flex-col items-center justify-center h-full text-center gap-2 px-6">
          <p className="text-ui-text2 text-sm font-medium">Nothing to show yet</p>
          <p className="text-ui-text3 text-xs">Ask SAM to list your tasks, properties or meetings — results appear here.</p>
        </div>
      )}
    </div>
  </div>
  </UserViewContext.Provider>
  );
};

export default UXView;
